import React, { useRef } from "react";
import { ChevronDown, HelpCircle, Zap, X, Loader2, Landmark, Users as UsersIcon, Pencil, Plus, Minus, Sparkles } from "lucide-react";
import useDefaultCountryCode from "../../../../components/hook/use-default-country-code";
import { toast } from "sonner";
import { ConditionGroup, ConditionRow, FlowNode, JourneyCategory, TriggerDef, TriggerInstance } from "./types";
import { findActionDef, findTriggerDef, JOURNEY_CATEGORIES } from "./catalog";
import { isGroupedCondition } from "./flow-model";
import { NODE_ICONS } from "./flow-canvas";
import { WhatsappMockUp } from "../../../../components/marketing/campaign/whatsapp-mock";
import { PhoneMockup } from "../../../../components/marketing/campaign/phone-mock";
import { EmailMockUp } from "../../../../components/marketing/campaign/email-mockup";
import {
  AudienceCondition,
  EmailTemplateOption,
  JourneyChannel,
  useAiGenerate,
  useAssigners,
  useAudiencePreview,
  useConditionOptions,
  useEmailTemplates,
  usePushTemplates,
  useProfileAttributes,
  useSmsTemplates,
  useTriggerEvents,
  useTriggerOptions,
  useVariables,
  useWhatsAppTemplates,
  WhatsAppTemplateOption,
} from "./journey-api";
import { EmailActionModal, EmailDesignFields, buildDefaultEmailHtml } from "./email-action-editor";
import { PlainTextEditor } from "../../../../components/shared/plain-text-editor";

const CHANNEL_TO_API: Record<string, JourneyChannel> = { WhatsApp: "whatsapp", SMS: "sms", Email: "email" };

const inputCls =
  "w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/20";

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="mb-3.5">
    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">{label}</label>
    {children}
  </div>
);

// `labels` lets a caller show a friendlier label than the raw option value
// (e.g. an SMS campaign's UUID as the value, its name as what's shown).
// `disabledReasons` disables an option outright (not just an annotated
// label) — for an attribute the sweep resolver can't query yet, offering it
// builds a filter that will be rejected with a 400, so it's greyed out with
// the real reason as the option's native tooltip.
const SelectField: React.FC<{ value: string; onChange: (v: string) => void; options: string[]; labels?: Record<string, string>; disabledReasons?: Record<string, string> }> = ({
  value,
  onChange,
  options,
  labels,
  disabledReasons,
}) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
    {options.map((o) => (
      <option key={o} value={o} disabled={!!disabledReasons?.[o]} title={disabledReasons?.[o]}>
        {labels?.[o] ?? o}
      </option>
    ))}
  </select>
);

// "rm_retention" → "RM retention"
const humanizeId = (id: string) => id.replace(/_/g, " ").replace(/^rm\b/i, "RM").replace(/^\w/, (ch) => ch.toUpperCase());

// Fetch Profile's Lookup key — label shown, warehouse key in quotes.
const LOOKUP_KEYS: Record<string, string> = {
  "Customer ID": "customer_id",
  "Subscriber ID": "subscriber_id",
  Phone: "phone",
  Email: "email",
  "Lead ID": "lead_id",
};

// Telco journeys (data_balance_low, bill_overdue, subscriber_inactivity_detected) key customers by subscriber, not
// customer id — detected from the journey/trigger since the catalog has no
// explicit vertical flag.
const isTelcoContext = (...hints: (string | undefined)[]) =>
  /data[\s_]balance|bill[\s_]overdue|subscriber|telco|telecom|bundle|airtime/i.test(hints.filter(Boolean).join(" "));

// Data balances are megabytes, not money — no currency symbol on their tiers.
const isDataAttribute = (id: string) => /data[\s_]balance|_mb$|megabyte/i.test(id);
// Overdue / inactivity are day counts — no currency symbol either.
const isOverdueAttribute = (id: string) => /days[\s_]overdue|overdue[\s_]days/i.test(id);
const isInactivityAttribute = (id: string) => /inactiv(?:e|ity)[\s_]days|days[\s_]inactive/i.test(id);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[11px] font-semibold tracking-wide text-gray-400 dark:text-gray-500 mb-2 mt-5 first:mt-0">{children}</p>
);

// A "Title { optional } +" row that opens to its content (Figma, fraud
// alert trigger panel). Starts open when there's already something set.
const CollapsibleSection: React.FC<{ title: string; defaultOpen?: boolean; children: React.ReactNode }> = ({ title, defaultOpen, children }) => {
  const [open, setOpen] = React.useState(!!defaultOpen);
  return (
    <div className="border-y border-gray-200 dark:border-gray-800 mt-4">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between py-3.5 px-1 text-sm font-semibold text-gray-800 dark:text-gray-100">
        <span>
          {title} <span className="font-semibold">{"{ optional }"}</span>
        </span>
        {open ? <Minus size={16} className="text-gray-400" /> : <Plus size={16} className="text-gray-400" />}
      </button>
      {open && <div className="pb-3 px-1">{children}</div>}
    </div>
  );
};

const Checkbox: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }> = ({ checked, onChange, label, disabled }) => (
  <label className={`flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 py-1 ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
    />
    {label}
  </label>
);

const Radio: React.FC<{ checked: boolean; onChange: () => void; label: string }> = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 py-1 cursor-pointer">
    <input type="radio" checked={checked} onChange={onChange} className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500" />
    {label}
  </label>
);

// A single radio rendered on its own, standing in for a binary "end workflow
// here / continue to next step" toggle — matches the reference design, which
// shows just one "End workflow" radio per outcome rather than a visible pair.
// Clicking it while already checked switches back to "continue".
const ToggleRadio: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string }> = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 py-1 cursor-pointer">
    <input type="radio" checked={checked} onChange={() => onChange(!checked)} className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500" />
    {label}
  </label>
);

const ConfigHeader: React.FC<{ icon: React.ComponentType<any>; title: string; subtitle?: string }> = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-2.5 mb-4">
    <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
      <Icon size={15} className="text-gray-600 dark:text-gray-300" />
    </div>
    <div className="min-w-0">
      <p className="text-sm font-bold text-gray-900 dark:text-gray-100 uppercase tracking-tight truncate">{title}</p>
      {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

// ─── Message content — shared by AI Generate and every Send node ──────────
// The multi-channel picker, the phone preview, "Regenerate" and "Shorten
// URL" don't call anything real yet — this is still the hardcoded builder
// (see builder-page.tsx's Save draft/Publish toasts) for those. "Add
// variable" (VariablePicker below) is real: GET /v1/bot/variables.

const CHANNEL_OPTIONS_ALL = ["WhatsApp", "SMS", "Email", "RCS"];

// Real GET /v1/bot/variables — replaces the old single hardcoded
// "{{customer.name}}" button with the actual grouped, insertable merge
// fields (Customer/Bank/Links/Loan/Transaction/...), each wrapped in {{...}}
// exactly as the backend declares its id.
const VariablePicker: React.FC<{ onInsert: (token: string) => void }> = ({ onInsert }) => {
  const { groups, loading } = useVariables();
  const [open, setOpen] = React.useState(false);
  const ref = useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline">
        Add variable
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-56 max-h-72 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg py-1">
          {loading ? (
            <p className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5">
              <Loader2 size={11} className="animate-spin" /> Loading…
            </p>
          ) : groups.length === 0 ? (
            <p className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500">No variables available.</p>
          ) : (
            groups.map((g) => (
              <div key={g.group}>
                <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">{g.group}</p>
                {g.items.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => {
                      onInsert(`{{${v.id}}}`);
                      setOpen(false);
                    }}
                    className="flex flex-col w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                  >
                    <span className="font-medium">{v.label}</span>
                    {v.example && <span className="text-gray-400 dark:text-gray-500 truncate">e.g. {v.example}</span>}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const ChannelMultiSelect: React.FC<{ selected: string[]; onToggle: (ch: string) => void }> = ({ selected, onToggle }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="mb-2">
      <button type="button" onClick={() => setOpen((o) => !o)} className={`${inputCls} flex items-center justify-between text-left`}>
        <span>
          {selected[0] || "Select"}
          {selected.length > 1 ? ` + ${selected.length - 1} more` : ""}
        </span>
        <ChevronDown size={14} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>
      {open && (
        <div className="mt-1 border border-gray-200 dark:border-gray-700 rounded-lg py-1 bg-white dark:bg-gray-900 shadow-sm">
          {CHANNEL_OPTIONS_ALL.map((ch) => (
            <label key={ch} className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
              <input type="checkbox" checked={selected.includes(ch)} onChange={() => onToggle(ch)} className="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600 text-indigo-600" />
              {ch}
            </label>
          ))}
        </div>
      )}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selected.map((ch) => (
            <span key={ch} className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200">
              {ch}
              {selected.length > 1 && (
                <button type="button" onClick={() => onToggle(ch)} className="hover:text-rose-500">
                  <X size={10} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// A selected real WhatsApp template's own body (with {{1}}/{{2}}
// placeholders), buttons and header always take over the preview from
// whatever's typed into Description/CTA — Meta sends exactly what the
// template declares, not free text. `vars` here are the template's own
// declared example values (same ones the Templates/Campaign preview shows
// for an untouched template) purely for readability — never real customer
// data, since none exists in this static builder view.
const buildTemplateVars = (t?: WhatsAppTemplateOption): Record<string, string> | undefined => {
  if (!t?.variables?.length) return undefined;
  const vars: Record<string, string> = {};
  t.variables.forEach((v) => {
    vars[`variable_${v.index}`] = v.example || v.name || "";
  });
  return vars;
};

const MessagePreviewEditor: React.FC<{
  channel: string;
  config: Record<string, any>;
  onChange: (patch: Record<string, any>) => void;
  templateName?: string;
  waTemplate?: WhatsAppTemplateOption;
  emailTemplate?: EmailTemplateOption;
  onRegenerate?: () => void;
  regenerating?: boolean;
  onOpenEmailModal?: () => void;
}> = ({ channel, config: c, onChange, templateName, waTemplate, emailTemplate, onRegenerate, regenerating, onOpenEmailModal }) => {
  const isSms = channel === "SMS";
  const isEmail = channel === "Email";
  const isPush = channel === "Push";
  const bodyText = isSms || isPush ? c.messageBody : c.description;
  const templateVars = React.useMemo(() => buildTemplateVars(waTemplate), [waTemplate]);

  // Preview priority: a design authored in the Email Editor tab wins outright
  // (it's the operator's own full custom markup); next, a real catalog
  // template's own HTML (don't wrap a template someone already designed);
  // otherwise this is a Plain Text email, which gets wrapped in the default
  // design (header image / CTA / footer) rather than shown as bare text —
  // matches the real content.email.html_body field sent on test-run/send, so
  // the preview is what actually goes out, not a fictional view of it.
  const emailHtml = isEmail
    ? c.useCustomDesign && c.customHtml
      ? c.customHtml
      : emailTemplate?.html
        ? emailTemplate.html
        : buildDefaultEmailHtml({
            headerImageUrl: c.headerImageUrl,
            bodyHtml: c.description || "",
            ctaEnabled: c.ctaEnabled,
            ctaText: c.ctaText,
            ctaColor: c.ctaColor,
            footerEnabled: c.footerEnabled,
            websiteLink: c.websiteLink,
            instagramLink: c.instagramLink,
            facebookLink: c.facebookLink,
            twitterLink: c.twitterLink,
            footerText: c.footerText,
          })
    : undefined;

  return (
    <div className="mt-5">
      <SectionLabel>Preview</SectionLabel>
      <div className="rounded-lg border border-gray-100 dark:border-gray-800 overflow-hidden mb-3">
        <div className="bg-emerald-50/60 dark:bg-emerald-500/5 px-3 py-2.5">
          <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{isPush ? "Push Notification" : channel}</p>
          {templateName && (
            <p className="text-xs font-semibold truncate mt-0.5">
              <span className="text-indigo-600 dark:text-indigo-400">Template : </span>
              <span className="text-gray-600 dark:text-gray-300">{templateName}</span>
            </p>
          )}
        </div>
        {/* Same phone mockups the campaign and template flows already use
            (components/marketing/campaign/{whatsapp,phone}-mock.tsx) — kept
            in sync with them automatically rather than a bot-builder-only copy. */}
        <div className="bg-gray-100 dark:bg-gray-950 py-3">
          {isSms ? (
            <PhoneMockup message={bodyText || undefined} />
          ) : isEmail ? (
            <EmailMockUp subject={c.title || undefined} senderName="VouchLinks Stores" htmlContent={emailHtml} />
          ) : isPush ? (
            <div className="px-4">
              <div className="flex items-start gap-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-3">
                <div className="w-9 h-9 rounded-full bg-indigo-600 flex items-center justify-center shrink-0">
                  <Landmark size={16} className="text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{c.title || "Notification title"}</p>
                    <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">9:41 AM</span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-200 mt-0.5">{bodyText || "Notification body"}</p>
                </div>
              </div>
            </div>
          ) : (
            <WhatsappMockUp
              message={bodyText || undefined}
              template={
                waTemplate
                  ? { originalContent: waTemplate.content, buttons: waTemplate.buttons, header_type: waTemplate.header_type, header_content: waTemplate.header_content }
                  : undefined
              }
              vars={templateVars}
              cta={!waTemplate && c.ctaText1 ? { label: c.ctaText1, url: c.ctaLink1 } : undefined}
            />
          )}
        </div>
      </div>
      {/* Only when an AI step feeds this card — with a fixed template
          (e.g. the fraud alert) there's nothing to regenerate. */}
      {onRegenerate && (
        <button
          type="button"
          disabled={!!regenerating}
          onClick={onRegenerate}
          className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline mb-4 disabled:opacity-60"
        >
          {regenerating && <Loader2 size={12} className="animate-spin" />} Regenerate Again
        </button>
      )}

      {isSms ? (
        <>
          <Field label="Message Body">
            <textarea value={c.messageBody || ""} onChange={(e) => onChange({ messageBody: e.target.value })} rows={4} maxLength={161} className={`${inputCls} resize-none`} />
          </Field>
          <div className="flex items-center justify-between -mt-2.5 mb-3.5">
            <span className="text-[10px] text-gray-400 dark:text-gray-500">{(c.messageBody || "").length}/161 Characters</span>
            <VariablePicker onInsert={(token) => onChange({ messageBody: `${c.messageBody || ""}${token}` })} />
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-700 dark:text-gray-200">Shorten URL</span>
            <button
              type="button"
              role="switch"
              aria-checked={!!c.shortenUrl}
              onClick={() => onChange({ shortenUrl: !c.shortenUrl })}
              className={`relative w-9 h-5 rounded-full transition-colors ${c.shortenUrl ? "bg-indigo-600" : "bg-gray-300 dark:bg-gray-600"}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${c.shortenUrl ? "translate-x-4" : ""}`} />
            </button>
          </div>
          {c.shortenUrl && (
            <p className="text-[11px] text-gray-400 dark:text-gray-500">
              Full: <span className="text-gray-600 dark:text-gray-300">https://www.dalmlabs.com</span> · Short: <span className="text-indigo-600 dark:text-indigo-400">bit.ly/lksh</span>
            </p>
          )}
        </>
      ) : isEmail ? (
        <>
          <div className="flex items-center justify-between mb-1">
            <SectionLabel>Content</SectionLabel>
            {onOpenEmailModal && (
              <button type="button" onClick={onOpenEmailModal} className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline">
                Edit Email
              </button>
            )}
          </div>
          <Field label="Subject">
            <input value={c.title || ""} onChange={(e) => onChange({ title: e.target.value })} placeholder="e.g. Loan Offer" className={inputCls} />
          </Field>
          <Field label="Message Body">
            <PlainTextEditor value={c.description || ""} onChange={(v) => onChange({ description: v })} />
          </Field>
          <div className="flex items-center justify-between -mt-2.5 mb-3.5">
            <span className="text-[10px] text-gray-400 dark:text-gray-500">{(c.description || "").length}/161 Characters</span>
            <VariablePicker onInsert={(token) => onChange({ description: `${c.description || ""}${token}` })} />
          </div>
          <EmailDesignFields config={c} onChange={onChange} />
        </>
      ) : isPush ? (
        <>
          <Field label="Title">
            <input value={c.title || ""} onChange={(e) => onChange({ title: e.target.value })} placeholder="e.g. Alert! Needs your attention" className={inputCls} />
          </Field>
          <Field label="Message Body">
            <textarea value={c.messageBody || ""} onChange={(e) => onChange({ messageBody: e.target.value })} rows={4} maxLength={161} className={`${inputCls} resize-none`} />
          </Field>
          <div className="flex items-center justify-between -mt-2.5 mb-3.5">
            <span className="text-[10px] text-gray-400 dark:text-gray-500">{(c.messageBody || "").length}/161 Characters</span>
            <VariablePicker onInsert={(token) => onChange({ messageBody: `${c.messageBody || ""}${token}` })} />
          </div>
        </>
      ) : (
        <>
          <Field label="Title">
            <input value={c.title || ""} onChange={(e) => onChange({ title: e.target.value })} placeholder="Here goes title" className={inputCls} />
          </Field>
          <Field label="Description">
            <textarea value={c.description || ""} onChange={(e) => onChange({ description: e.target.value })} rows={4} maxLength={300} className={`${inputCls} resize-none`} />
          </Field>
          <div className="flex items-center justify-between -mt-2.5 mb-3.5">
            <span className="text-[10px] text-gray-400 dark:text-gray-500">{(c.description || "").length}/300 Characters</span>
            <VariablePicker onInsert={(token) => onChange({ description: `${c.description || ""}${token}` })} />
          </div>
          <Field label="CTA Text-1">
            <input value={c.ctaText1 || ""} onChange={(e) => onChange({ ctaText1: e.target.value })} placeholder="Explore the offer" className={inputCls} />
          </Field>
          <Field label="CTA Link-1">
            <input value={c.ctaLink1 || ""} onChange={(e) => onChange({ ctaLink1: e.target.value })} placeholder="www.example.com" className={inputCls} />
          </Field>
        </>
      )}
    </div>
  );
};

// ─── Trigger config ─────────────────────────────────────────────────────────
// Generic for every trigger — real GET /v1/bot/trigger-options (confirmed
// live, see useTriggerOptions in journey-api.ts; not listed in the host's own
// /openapi.json, so verify with a direct curl rather than trusting that doc
// for this one endpoint) replaces the old JSON URL/upload panel for a sample
// payload that never called anything real. Workflow Settings (priority/
// timeout) below is separate and stays fraud_anomaly_alert-only.

// How far the pointer has to move (px) before a press-and-hold turns into a
// drag rather than a click — same threshold and same hand-rolled Pointer
// Events approach as add-panel.tsx (native HTML5 DnD doesn't reliably fire
// for trackpads), applied here to the trigger catalog instead of actions.
const TRIGGER_DRAG_THRESHOLD = 4;

// Real triggers from GET /v1/bot/triggers (JOURNEY_CATEGORIES, catalog.ts),
// shown only while no trigger is picked yet (trigger.id === "") — see
// builder-page.tsx's initialTrigger for when that's the case. Every item
// supports the same two ways in as the action palette: click it, or
// press-drag it onto the canvas's own dashed trigger box.
const TriggerPicker: React.FC<{
  onPick: (def: TriggerDef, category: JourneyCategory) => void;
  onDragStart: (def: TriggerDef, category: JourneyCategory, x: number, y: number) => void;
  onDragMove: (x: number, y: number) => void;
  onDragEnd: (x: number, y: number) => void;
}> = ({ onPick, onDragStart, onDragMove, onDragEnd }) => {
  const pressRef = useRef<{ def: TriggerDef; category: JourneyCategory; startX: number; startY: number; dragging: boolean; pointerId: number } | null>(null);

  const handlePointerDown = (def: TriggerDef, category: JourneyCategory) => (e: React.PointerEvent<HTMLButtonElement>) => {
    if (def.status === "planned") return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    pressRef.current = { def, category, startX: e.clientX, startY: e.clientY, dragging: false, pointerId: e.pointerId };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const press = pressRef.current;
    if (!press || press.pointerId !== e.pointerId) return;
    if (!press.dragging) {
      if (Math.hypot(e.clientX - press.startX, e.clientY - press.startY) < TRIGGER_DRAG_THRESHOLD) return;
      press.dragging = true;
      onDragStart(press.def, press.category, e.clientX, e.clientY);
    }
    e.preventDefault();
    onDragMove(e.clientX, e.clientY);
  };
  const handlePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const press = pressRef.current;
    if (!press || press.pointerId !== e.pointerId) return;
    pressRef.current = null;
    if (press.dragging) onDragEnd(e.clientX, e.clientY);
    else onPick(press.def, press.category);
  };
  const handlePointerCancel = (e: React.PointerEvent<HTMLButtonElement>) => {
    const press = pressRef.current;
    if (!press || press.pointerId !== e.pointerId) return;
    pressRef.current = null;
    if (press.dragging) onDragEnd(-1, -1);
  };
  const dragProps = (def: TriggerDef, category: JourneyCategory) => ({
    onPointerDown: handlePointerDown(def, category),
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
    style: { touchAction: "none" as const },
  });

  if (JOURNEY_CATEGORIES.length === 0) {
    return (
      <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5 py-2">
        <Loader2 size={12} className="animate-spin" /> Loading triggers…
      </p>
    );
  }

  return (
    <div>
      <SectionLabel>Select the Trigger</SectionLabel>
      {JOURNEY_CATEGORIES.map((cat) => (
        <div key={cat.id} className="mb-5">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2.5 flex items-center gap-1.5">
            <span>{cat.icon}</span> {cat.label}
          </p>
          <div className="grid grid-cols-2 gap-2.5">
            {cat.triggers.map((t) => {
              const isPlanned = t.status === "planned";
              return (
                <button
                  key={t.id}
                  type="button"
                  {...dragProps(t, cat)}
                  title={isPlanned ? "Coming soon" : "Click, or press and drag this onto the canvas"}
                  className={`flex flex-col items-center gap-1.5 py-3.5 px-2 rounded-xl border border-gray-200 dark:border-gray-700 text-center select-none transition-colors ${
                    isPlanned
                      ? "opacity-40 cursor-not-allowed"
                      : "hover:border-indigo-300 hover:bg-indigo-50/50 dark:hover:bg-indigo-500/10 cursor-grab active:cursor-grabbing"
                  }`}
                >
                  <UsersIcon size={18} className="text-gray-400 dark:text-gray-500 shrink-0" />
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-200 leading-tight">{t.label}</span>
                  {isPlanned && <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500">Soon</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// Plain comparison operators for an Event trigger's own property filters
// ("optional filters on the event's own properties, e.g. amount > 150000").
// GET /v1/bot/trigger-events now enumerates each event's own filterable
// fields (name + type) via `filterable_properties` — the field picker below
// uses that — but it doesn't enumerate per-field operators, so this remains
// the smallest literal reading of that one example rather than an invented
// per-field operator list.
const EVENT_CONDITION_OPERATORS = [
  { id: "gt", label: ">" },
  { id: "gte", label: "≥" },
  { id: "lt", label: "<" },
  { id: "lte", label: "≤" },
  { id: "eq", label: "=" },
  { id: "neq", label: "≠" },
];

interface EventCondition {
  field: string;
  operator: string;
  value: string;
}

// "Who will enter this journey" — POST /v1/bot/audience/preview (debounced on
// every condition edit), confirmed live against the real warehouse. Shows the
// first few of the preview's own `sample`; "Show all" expands the rest of
// that same short list in place and "Hide" collapses it again — no second
// request, no separate page. Never shows `matched` on its own — an operator
// told "34 customers" when only 16 are reachable believes they're reaching
// twice as many people as they really are.
const AUDIENCE_SAMPLE_PREVIEW = 3;
const AudiencePreviewPanel: React.FC<{ conditions: AudienceCondition[] }> = ({ conditions }) => {
  const { preview } = useAudiencePreview();
  const [result, setResult] = React.useState<{ matched: number; reachable: number; unreachable: number; note?: string; historyNote?: string; sample: { name: string; phone?: string; email?: string }[] } | null>(null);
  const [previewError, setPreviewError] = React.useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [showAll, setShowAll] = React.useState(false);

  const ready = conditions.length > 0 && conditions.every((c) => c.attribute && c.operator && c.value !== "" && c.value != null);
  const conditionsKey = JSON.stringify(conditions);

  // Debounced — cheap enough to call on every edit per the brief, but no
  // need to fire on every keystroke while a number field is still being typed.
  React.useEffect(() => {
    if (!ready) {
      setResult(null);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      const { result: res, error } = await preview(conditions);
      if (cancelled) return;
      setPreviewLoading(false);
      if (res) {
        setResult({
          matched: res.matched,
          reachable: res.reachable,
          unreachable: res.unreachable,
          note: res.note,
          historyNote: res.needs_history ? res.history_note : undefined,
          sample: res.sample.map((s) => ({ name: [s.first_name, s.last_name].filter(Boolean).join(" ") || "—", phone: s.phone, email: s.email })),
        });
        setPreviewError(null);
      } else {
        setResult(null);
        setPreviewError(error);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditionsKey, ready]);

  if (!ready) return null;

  const visibleSample = result ? (showAll ? result.sample : result.sample.slice(0, AUDIENCE_SAMPLE_PREVIEW)) : [];
  const hasMore = !!result && result.sample.length > AUDIENCE_SAMPLE_PREVIEW;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-3 mt-1 mb-4">
      <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2">Who will enter this journey</p>
      {previewLoading && !result ? (
        <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5 py-1">
          <Loader2 size={11} className="animate-spin" /> Checking against the live customer base…
        </p>
      ) : previewError ? (
        <p className="text-xs text-rose-600 dark:text-rose-400 leading-snug">{previewError}</p>
      ) : !result ? (
        <p className="text-xs text-gray-400 dark:text-gray-500">Couldn't reach the audience check — try again.</p>
      ) : (
        <>
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {result.reachable} of {result.matched} matching customers
          </p>
          {result.note && <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{result.note}</p>}
          {result.historyNote && (
            <div className="rounded-md bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 px-2.5 py-1.5 mt-2">
              <p className="text-[11px] text-amber-800 dark:text-amber-300 leading-snug">{result.historyNote}</p>
            </div>
          )}
          {visibleSample.length > 0 && (
            <div className="mt-2.5 space-y-1">
              {visibleSample.map((s, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-gray-700 dark:text-gray-200 truncate">{s.name}</span>
                  <span className="text-gray-400 dark:text-gray-500 truncate ml-2">{s.phone || s.email || "—"}</span>
                </div>
              ))}
            </div>
          )}
          {hasMore && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              aria-expanded={showAll}
              className="flex items-center gap-1 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline mt-2"
            >
              {showAll ? "Hide" : "Show all"}
              <ChevronDown size={12} className={showAll ? "rotate-180 transition-transform" : "transition-transform"} />
            </button>
          )}
        </>
      )}
    </div>
  );
};

// Condition rows: a numeric attribute compared with a less/greater operator.
const isNumericAttribute = (a?: { type?: string; default_threshold?: unknown; compare_to?: string }) =>
  !!a && (/^(number|numeric|integer|int|float|decimal|mb|amount)$/i.test(a.type || "") || !!a.default_threshold || /threshold/i.test(a.compare_to || ""));
const isComparisonOperator = (op: string) => /less|greater|^(lt|lte|gt|gte)$|above|below|at_least|at_most/i.test(op);

// Which trait attributes suit a journey, from its id/trigger name. Used to
// pick a sensible default and to warn when a telco journey filters on a
// banking trait.
const TELCO_JOURNEY = /data[\s_]?balance|airtime|bill[\s_]?overdue|inactiv|subscriber|recharge|telco/i;
const BANKING_ATTRIBUTE = /credit[\s_]?score|salary|loan|kyc|bvn|deposit|account[\s_]?balance|ltv|lifetime/i;
const isTelcoJourney = (...keys: (string | undefined)[]) => TELCO_JOURNEY.test(keys.filter(Boolean).join(" "));
function journeyAttributePattern(...keys: (string | undefined)[]): RegExp | null {
  const k = keys.filter(Boolean).join(" ");
  if (/airtime/i.test(k)) return /airtime/i;
  if (/data[\s_]?balance/i.test(k)) return /data/i;
  if (/inactiv/i.test(k)) return /inactiv|last[\s_]?activ|dormant/i;
  if (/bill[\s_]?overdue|overdue/i.test(k)) return /overdue|bill/i;
  return null;
}

// The real Event-vs-Trait trigger config, backed by GET /v1/bot/trigger-options
// (useTriggerOptions, journey-api.ts). Every key this writes into
// trigger.config (kind/event/conditions, or attribute/operator/value/value_to/
// schedule/re_entry/debounce_days) is spelled exactly like the endpoint's own
// `config` field names for that kind — not a shape invented on this side —
// since the real audience resolver that runs a trait trigger needs to agree
// with this panel on it (confirmed live, see AudiencePreviewPanel above).
const TriggerBehaviorConfig: React.FC<{
  trigger: TriggerInstance;
  onChange: (patch: Record<string, any>) => void;
  savedJourneyId?: number;
  journeyId?: string;
}> = ({ trigger, onChange, savedJourneyId, journeyId }) => {
  const { data, loading } = useTriggerOptions();
  const { data: eventsData, loading: eventsLoading } = useTriggerEvents();
  const c = trigger.config || {};
  const kind: string = c.kind || "event";

  // Seed a sane default once the real options load — same "only when nothing
  // picked yet" pattern as profileAttrs/assigners elsewhere in this file.
  // Event choice itself is seeded separately below once trigger-events loads.
  React.useEffect(() => {
    if (!data || c.kind) return;
    onChange({ kind: "event" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // GET /v1/bot/trigger-events replaces trigger-options' own generic `events`
  // for the event-kind dropdown — seed the first one once it loads.
  React.useEffect(() => {
    if (!eventsData || kind !== "event" || c.event) return;
    const first = eventsData.groups[0]?.events[0];
    if (first) onChange({ event: first.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsData, kind, c.event]);

  // Trait requires an attribute+operator (what selects anyone at all) and a
  // schedule+re_entry ("required — nothing fires a trait trigger" / "the
  // customer still matches next sweep") to actually publish. Unlike the
  // display-only `|| data.X[0]` fallbacks below (which only cover the very
  // first paint), this writes real values into trigger.config — otherwise a
  // trait trigger saved without the operator ever touching these dropdowns
  // would publish with them left undefined. Only fills gaps; never overwrites
  // a value the operator already chose. Must run before the loading/null
  // early returns below (Rules of Hooks), so it guards on `data` itself.
  React.useEffect(() => {
    if (!data || kind !== "trait") return;
    const patch: Record<string, any> = {};
    let attrId = c.attribute;
    if (!attrId) {
      // A telco journey used to get the catalog's first attribute — a banking
      // trait like credit_score that no subscriber matches. Prefer one that
      // fits the journey; fall back to the first queryable one.
      const queryable = data.trait_attributes.filter((a) => a.queryable !== false);
      const fits = journeyAttributePattern(journeyId, trigger.id, trigger.label);
      const firstAttr =
        (fits && queryable.find((a) => fits.test(`${a.id} ${a.label}`))) || queryable[0] || data.trait_attributes[0];
      attrId = firstAttr?.id;
      if (attrId) patch.attribute = attrId;
    }
    if (attrId && !c.operator) {
      const attr = data.trait_attributes.find((a) => a.id === attrId);
      if (attr?.operators[0]) patch.operator = attr.operators[0].id;
    }
    if (!c.schedule && data.schedules[0]) patch.schedule = { id: data.schedules[0].id, ...data.schedules[0].default };
    if (!c.re_entry && data.re_entry[0]) patch.re_entry = { id: data.re_entry[0].id, ...(data.re_entry[0].default || {}) };
    if (Object.keys(patch).length > 0) onChange(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, kind, c.attribute, c.operator, c.schedule, c.re_entry]);

  // New Figma named-group shape for event filters (20 Oct) — a brand-new
  // event trigger (nothing saved under either the old `conditions` flat
  // array or this new `eventFilterGroups`) starts on the new shape; a
  // trigger that already has real flat `conditions` saved keeps rendering
  // through the old UI untouched, same backward-compat approach as the
  // Condition node above.
  React.useEffect(() => {
    if (!data || kind !== "event") return;
    if (Array.isArray(c.eventFilterGroups) || Array.isArray(c.conditions)) return;
    onChange({ eventFilterGroups: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, kind, c.eventFilterGroups, c.conditions]);

  if (loading && !data) {
    return (
      <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5 py-2 mb-2">
        <Loader2 size={12} className="animate-spin" /> Loading trigger options…
      </p>
    );
  }
  if (!data) return null;

  const selectedKind = data.kinds.find((k) => k.id === kind);
  const attrDisabledReasons = Object.fromEntries(
    data.trait_attributes.filter((a) => a.queryable === false).map((a) => [a.id, a.unavailable_reason || "Not available on this warehouse yet."])
  );
  const selectedAttribute = data.trait_attributes.find((a) => a.id === c.attribute);
  const selectedOperator = selectedAttribute?.operators.find((o) => o.id === c.operator);
  const needsTwoValues = (selectedOperator?.takes ?? 1) >= 2;
  const selectedSchedule = data.schedules.find((s) => s.id === c.schedule?.id) || data.schedules[0];
  const selectedReEntry = data.re_entry.find((r) => r.id === c.re_entry?.id) || data.re_entry[0];
  const allEvents = eventsData?.groups.flatMap((g) => g.events) || [];
  // A real journey's own trigger node can carry an older event id that's
  // since been renamed — confirmed live via GET /v1/bot/trigger-events'
  // `aliases` (e.g. salary_day_loan's own stored "salary_credit_detected" ->
  // current "salary_credited"). The backend still accepts the old id fine,
  // but a native <select> whose `value` matches no <option> just falls back
  // to showing its first option instead — not blank, a DIFFERENT real
  // event's name, which is why opening salary_day_loan showed "Account
  // Opened"/whatever else happened to be first. Resolved here for display
  // only; `c.event` itself is left exactly as the journey stored it.
  const displayEventId = allEvents.some((e) => e.id === c.event) ? c.event : eventsData?.aliases?.[c.event] || c.event;
  const selectedEvent = allEvents.find((e) => e.id === displayEventId);
  const audienceFilter: (AudienceCondition & { value_to?: string | number })[] = Array.isArray(c.audience) ? c.audience : [];
  const updateAudienceFilter = (i: number, patch: Partial<AudienceCondition> & { value_to?: any }) =>
    onChange({ audience: audienceFilter.map((row, idx) => (idx === i ? { ...row, ...patch } : row)) });
  const addAudienceFilter = () => {
    const first = data.trait_attributes.find((a) => a.queryable !== false) || data.trait_attributes[0];
    onChange({ audience: [...audienceFilter, { attribute: first?.id || "", operator: first?.operators[0]?.id || "", value: "" }] });
  };
  const removeAudienceFilter = (i: number) => onChange({ audience: audienceFilter.filter((_, idx) => idx !== i) });

  const setSchedule = (scheduleId: string) => {
    const sched = data.schedules.find((s) => s.id === scheduleId);
    onChange({ schedule: sched ? { id: sched.id, ...sched.default } : { id: scheduleId } });
  };
  const setScheduleField = (field: string, value: string) => onChange({ schedule: { ...(c.schedule || { id: selectedSchedule?.id }), [field]: value } });
  const setReEntry = (reEntryId: string) => {
    const re = data.re_entry.find((r) => r.id === reEntryId);
    onChange({ re_entry: re ? { id: re.id, ...(re.default || {}) } : { id: reEntryId } });
  };
  const setReEntryField = (field: string, value: string) => onChange({ re_entry: { ...(c.re_entry || { id: selectedReEntry?.id }), [field]: value } });

  const conditions: EventCondition[] = Array.isArray(c.conditions) ? c.conditions : [];
  const updateCondition = (i: number, patch: Partial<EventCondition>) => onChange({ conditions: conditions.map((cond, idx) => (idx === i ? { ...cond, ...patch } : cond)) });
  const addCondition = () => onChange({ conditions: [...conditions, { field: "", operator: "gt", value: "" }] });
  const removeCondition = (i: number) => onChange({ conditions: conditions.filter((_, idx) => idx !== i) });

  // New Figma named-group filters on the event's own filterable_properties
  // (20 Oct) — same ConditionGroup/ConditionRow shape as the Condition node,
  // stored under a distinct config key (`eventFilterGroups`, not
  // `conditionGroups`) so the two don't collide. `row.attribute` here holds a
  // property field name (e.g. "amount"), not a trait-attribute id.
  const isGroupedFilters = Array.isArray(c.eventFilterGroups);
  const filterGroups: ConditionGroup[] = c.eventFilterGroups || [];
  const filterProps = selectedEvent?.filterable_properties || [];
  const updateFilterGroups = (next: ConditionGroup[]) => onChange({ eventFilterGroups: next });
  const updateFilterGroup = (gi: number, patch: Partial<ConditionGroup>) => updateFilterGroups(filterGroups.map((g, i) => (i === gi ? { ...g, ...patch } : g)));
  const updateFilterRow = (gi: number, ri: number, patch: Partial<ConditionRow>) =>
    updateFilterGroup(gi, { rows: filterGroups[gi].rows.map((r, i) => (i === ri ? { ...r, ...patch } : r)) });
  const addFilterRow = (gi: number) =>
    updateFilterGroup(gi, { rows: [...filterGroups[gi].rows, { id: `row_${Date.now().toString(36)}`, attribute: filterProps[0]?.field || "", operator: "gt", value: "" }] });
  const removeFilterRow = (gi: number, ri: number) => updateFilterGroup(gi, { rows: filterGroups[gi].rows.filter((_, i) => i !== ri) });
  const addFilterGroup = () =>
    updateFilterGroups([
      ...filterGroups,
      {
        id: `group_${Date.now().toString(36)}`,
        name: `Filter ${filterGroups.length + 1}`,
        matchLogic: "all",
        rows: [{ id: "row_0", attribute: filterProps[0]?.field || "", operator: "gt", value: "" }],
      },
    ]);
  const removeFilterGroup = (gi: number) => updateFilterGroups(filterGroups.filter((_, i) => i !== gi));

  return (
    <div className="mb-5">
      {/* Restyled from a big selectable-card grid into a compact "Trigger
          Source" dropdown (Figma redesign, 20 Oct) — still picks the real
          `kind` (event vs trait), just a different control. The design's own
          literal "Banking Events" copy has no backing live field anywhere
          (kind labels are "When something happens"/"When a profile
          matches") — this shows the real label from GET /v1/bot/trigger-options
          rather than a hardcoded string that doesn't correspond to anything. */}
      <Field label="Trigger Source">
        <SelectField
          value={kind}
          onChange={(v) => onChange({ kind: v })}
          options={data.kinds.map((k) => k.id)}
          labels={Object.fromEntries(data.kinds.map((k) => [k.id, k.label]))}
        />
      </Field>
      {selectedKind?.description && <p className="text-[11px] text-gray-400 dark:text-gray-500 -mt-2.5 mb-3.5">{selectedKind.description}</p>}

      {kind === "event" ? (
        <>
          <Field label="Trigger">
            {eventsLoading && !eventsData ? (
              <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5 py-1">
                <Loader2 size={11} className="animate-spin" /> Loading events…
              </p>
            ) : (
              <select
                value={displayEventId || ""}
                onChange={(e) => {
                  // `label` isn't part of `trigger.config` (it's the top-level
                  // TriggerInstance.label this panel's own header, plus the
                  // canvas trigger card, both render as-is) — without also
                  // patching it here, switching the Trigger dropdown left it
                  // frozen on whatever event was first picked, so picking A,
                  // then B, then A again kept showing B's name until a reload
                  // happened to rebuild `trigger` from scratch. See
                  // handleChangeTrigger (builder-page.tsx) for the other half.
                  const picked = allEvents.find((ev) => ev.id === e.target.value);
                  onChange({ event: e.target.value, label: picked?.label });
                }}
                className={inputCls}
              >
                {(eventsData?.groups || []).map((g) => (
                  <optgroup key={g.id} label={g.label}>
                    {g.events.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            )}
          </Field>
          {selectedEvent?.description && <p className="text-[11px] text-gray-400 dark:text-gray-500 -mt-2.5 mb-2">{selectedEvent.description}</p>}
          {selectedEvent?.derivation && (
            // Derived events are computed, not raw facts — the honest answer
            // to "where does this come from?" per the brief, so this stays
            // visible rather than tucked away.
            <p className="text-[11px] text-amber-600 dark:text-amber-400 -mt-1 mb-2 leading-snug">
              <span className="font-semibold">Derived: </span>
              {selectedEvent.derivation}
            </p>
          )}
          {selectedEvent?.example_payload && (
            <details className="-mt-1 mb-3">
              <summary className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 cursor-pointer select-none">Example payload</summary>
              <pre className="mt-1 text-[10px] leading-snug bg-gray-50 dark:bg-gray-950 border border-gray-100 dark:border-gray-800 rounded-md p-2 overflow-x-auto text-gray-600 dark:text-gray-300">
                {JSON.stringify(selectedEvent.example_payload, null, 2)}
              </pre>
            </details>
          )}

          <CollapsibleSection
            title="Additional filters"
            defaultOpen={conditions.length > 0 || filterGroups.some((g) => g.rows.some((r) => r.attribute && String(r.value ?? "") !== ""))}
          >
          {isGroupedFilters ? (
            <>
              {filterGroups.map((group, gi) => (
                <React.Fragment key={group.id}>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Group {gi + 1}</p>
                <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 px-3 py-3 mb-3">
                  <div className="flex items-center justify-between gap-2 mb-2.5">
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                      <input
                        value={group.name}
                        onChange={(e) => updateFilterGroup(gi, { name: e.target.value })}
                        className="text-xs font-bold uppercase tracking-wide text-gray-700 dark:text-gray-200 bg-transparent border-b border-transparent hover:border-gray-300 dark:hover:border-gray-600 focus:border-indigo-400 focus:outline-none min-w-0 flex-1"
                      />
                      <Pencil size={11} className="text-gray-400 shrink-0" />
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0 bg-gray-100 dark:bg-gray-900 rounded-md p-0.5">
                      <button
                        type="button"
                        onClick={() => updateFilterGroup(gi, { matchLogic: "all" })}
                        className={`text-[10px] font-bold px-2 py-1 rounded ${group.matchLogic === "all" ? "bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm" : "text-gray-500 dark:text-gray-400"}`}
                      >
                        ALL
                      </button>
                      <button
                        type="button"
                        onClick={() => updateFilterGroup(gi, { matchLogic: "any" })}
                        className={`text-[10px] font-bold px-2 py-1 rounded ${group.matchLogic === "any" ? "bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm" : "text-gray-500 dark:text-gray-400"}`}
                      >
                        ANY
                      </button>
                    </div>
                  </div>
                  {group.rows.map((row, ri) => (
                    <div key={row.id}>
                      {ri > 0 && <p className="text-[10px] font-bold text-center text-gray-400 dark:text-gray-500 my-1.5">{group.matchLogic === "all" ? "AND" : "OR"}</p>}
                      <div className="rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-3.5 py-3 mb-1.5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <label className="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 mb-1">CHECK IF</label>
                            {filterProps.length > 0 ? (
                              <select value={row.attribute} onChange={(e) => updateFilterRow(gi, ri, { attribute: e.target.value })} className={inputCls}>
                                {filterProps.map((p) => (
                                  <option key={p.field} value={p.field}>
                                    {p.field}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input value={row.attribute} onChange={(e) => updateFilterRow(gi, ri, { attribute: e.target.value })} placeholder="property, e.g. amount" className={inputCls} />
                            )}
                          </div>
                          {group.rows.length > 1 && (
                            <button type="button" onClick={() => removeFilterRow(gi, ri)} className="text-gray-300 hover:text-rose-500 shrink-0 mt-4">
                              <X size={14} />
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex-1">
                            <label className="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 mb-1">OPERATOR</label>
                            <select value={row.operator} onChange={(e) => updateFilterRow(gi, ri, { operator: e.target.value })} className={inputCls}>
                              {EVENT_CONDITION_OPERATORS.map((op) => (
                                <option key={op.id} value={op.id}>
                                  {op.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex-1">
                            <label className="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 mb-1">VALUE</label>
                            <input value={row.value} onChange={(e) => updateFilterRow(gi, ri, { value: e.target.value })} className={inputCls} />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button type="button" onClick={() => addFilterRow(gi)} className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline mt-1.5">
                    + Add another source
                  </button>
                  {filterGroups.length > 1 && (
                    <button type="button" onClick={() => removeFilterGroup(gi)} className="text-xs font-semibold text-gray-400 hover:text-rose-500 hover:underline mt-1.5 ml-3">
                      Remove group
                    </button>
                  )}
                </div>
                </React.Fragment>
              ))}
              <button
                type="button"
                onClick={addFilterGroup}
                className="w-full text-sm font-bold text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-xl py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 mb-1"
              >
                Add another condition group
              </button>
              {filterGroups.length === 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">No filters — fires on every {selectedEvent?.label || "event"}.</p>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mt-3 mb-1.5">
                <SectionLabel>Filters</SectionLabel>
                <button type="button" onClick={addCondition} className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline">
                  + Add filter
                </button>
              </div>
              {conditions.length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">No filters — fires on every {selectedEvent?.label || "event"}.</p>
              ) : (
                conditions.map((cond, i) => (
                  <div key={i} className="flex items-center gap-1.5 mb-2">
                    {selectedEvent && selectedEvent.filterable_properties.length > 0 ? (
                      <select value={cond.field} onChange={(e) => updateCondition(i, { field: e.target.value })} className={`${inputCls} flex-1`}>
                        <option value="">property…</option>
                        {selectedEvent.filterable_properties.map((p) => (
                          <option key={p.field} value={p.field}>
                            {p.field}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input value={cond.field} onChange={(e) => updateCondition(i, { field: e.target.value })} placeholder="property, e.g. amount" className={`${inputCls} flex-1`} />
                    )}
                    <select value={cond.operator} onChange={(e) => updateCondition(i, { operator: e.target.value })} className={`${inputCls} w-16`}>
                      {EVENT_CONDITION_OPERATORS.map((op) => (
                        <option key={op.id} value={op.id}>
                          {op.label}
                        </option>
                      ))}
                    </select>
                    <input value={cond.value} onChange={(e) => updateCondition(i, { value: e.target.value })} placeholder="value" className={`${inputCls} flex-1`} />
                    <button type="button" onClick={() => removeCondition(i)} className="text-gray-300 hover:text-rose-500 shrink-0">
                      <X size={14} />
                    </button>
                  </div>
                ))
              )}
            </>
          )}

          {selectedKind?.takes_audience_filter && (
            <>
              <div className="flex items-center justify-between mt-3 mb-1.5">
                <SectionLabel>Audience filter {selectedKind.takes_audience_filter === "optional" ? "(optional)" : ""}</SectionLabel>
                <button type="button" onClick={addAudienceFilter} className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline">
                  + Add filter
                </button>
              </div>
              <p className="text-[11px] text-gray-400 dark:text-gray-500 -mt-1 mb-2 leading-snug">
                The event already identified the customer — this can only exclude, never select.
              </p>
              {audienceFilter.length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">No audience filter — every matching {selectedEvent?.label || "event"} enters.</p>
              ) : (
                audienceFilter.map((row, i) => {
                  const attr = data.trait_attributes.find((a) => a.id === row.attribute);
                  const op = attr?.operators.find((o) => o.id === row.operator);
                  const needsTwo = (op?.takes ?? 1) >= 2;
                  return (
                    <div key={i} className="rounded-lg border border-gray-100 dark:border-gray-800 px-2.5 py-2 mb-2">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <SelectField
                          value={row.attribute}
                          onChange={(v) => {
                            const a = data.trait_attributes.find((x) => x.id === v);
                            updateAudienceFilter(i, { attribute: v, operator: a?.operators[0]?.id || "", value: "", value_to: undefined });
                          }}
                          options={data.trait_attributes.map((a) => a.id)}
                          labels={Object.fromEntries(data.trait_attributes.map((a) => [a.id, a.confirmed ? a.label : `${a.label} (unconfirmed)`]))}
                          disabledReasons={attrDisabledReasons}
                        />
                        <button type="button" onClick={() => removeAudienceFilter(i)} className="text-gray-300 hover:text-rose-500 shrink-0">
                          <X size={14} />
                        </button>
                      </div>
                      {attr && (
                        <div className="flex items-center gap-1.5">
                          <SelectField
                            value={row.operator || attr.operators[0]?.id || ""}
                            onChange={(v) => updateAudienceFilter(i, { operator: v })}
                            options={attr.operators.map((o) => o.id)}
                            labels={Object.fromEntries(attr.operators.map((o) => [o.id, o.label]))}
                          />
                          {attr.type === "band" ? (
                            <>
                              <SelectField value={(row.value as string) || attr.bands?.[0] || ""} onChange={(v) => updateAudienceFilter(i, { value: v })} options={attr.bands || []} />
                              {needsTwo && (
                                <>
                                  <span className="text-xs text-gray-400 shrink-0">to</span>
                                  <SelectField value={(row.value_to as string) || attr.bands?.[0] || ""} onChange={(v) => updateAudienceFilter(i, { value_to: v })} options={attr.bands || []} />
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              <input
                                type={attr.type === "number" ? "number" : "text"}
                                value={row.value ?? ""}
                                onChange={(e) => updateAudienceFilter(i, { value: e.target.value })}
                                className={`${inputCls} flex-1`}
                              />
                              {needsTwo && (
                                <>
                                  <span className="text-xs text-gray-400 shrink-0">to</span>
                                  <input
                                    type={attr.type === "number" ? "number" : "text"}
                                    value={row.value_to ?? ""}
                                    onChange={(e) => updateAudienceFilter(i, { value_to: e.target.value })}
                                    className={`${inputCls} flex-1`}
                                  />
                                </>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </>
          )}
          </CollapsibleSection>

          {/* UI-only, unwired placeholder (Figma redesign, 20 Oct) — per
              direct ask, built despite contradicting the live
              `takes_frequency: false` flag on the event kind and Anshul's own
              prior "frequency is nothing but a retry kind of a thing, remove
              that part for now." This never reads from or writes to any API
              call; its value lives only in trigger.config.runFrequencyPlaceholder,
              same "UI-only pending backend" precedent already used for AI
              Moderate's checks (catalog.ts). */}
          <CollapsibleSection title="Run frequency" defaultOpen={!!c.runFrequencyPlaceholder}>
            <input
              value={c.runFrequencyPlaceholder || ""}
              onChange={(e) => onChange({ runFrequencyPlaceholder: e.target.value })}
              placeholder="e.g. Once per customer per day"
              className={inputCls}
            />
          </CollapsibleSection>
        </>
      ) : (
        <>
          <Field label="Attribute">
            <SelectField
              value={c.attribute || data.trait_attributes[0]?.id || ""}
              onChange={(v) => onChange({ attribute: v, operator: undefined, value: undefined, value_to: undefined })}
              options={data.trait_attributes.map((a) => a.id)}
              labels={Object.fromEntries(data.trait_attributes.map((a) => [a.id, a.confirmed ? a.label : `${a.label} (unconfirmed)`]))}
              disabledReasons={attrDisabledReasons}
            />
          </Field>
          {selectedAttribute && selectedAttribute.queryable === false && (
            <p className="text-[11px] text-rose-600 dark:text-rose-400 -mt-2.5 mb-3.5">{selectedAttribute.unavailable_reason}</p>
          )}
          {selectedAttribute && isTelcoJourney(journeyId, trigger.id, trigger.label) && BANKING_ATTRIBUTE.test(`${selectedAttribute.id} ${selectedAttribute.label}`) && (
            <p className="text-[11px] text-rose-600 dark:text-rose-400 -mt-2.5 mb-3.5">
              {selectedAttribute.label} is a banking trait — no subscriber on this telco journey will match it. Pick a telco attribute instead.
            </p>
          )}
          {selectedAttribute && !selectedAttribute.confirmed && selectedAttribute.queryable !== false && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400 -mt-2.5 mb-3.5">Not yet verified on a live profile — this may return nothing until confirmed.</p>
          )}
          {selectedAttribute && (
            <>
              <Field label="Condition">
                <SelectField
                  value={c.operator || selectedAttribute.operators[0]?.id || ""}
                  onChange={(v) => onChange({ operator: v })}
                  options={selectedAttribute.operators.map((o) => o.id)}
                  labels={Object.fromEntries(selectedAttribute.operators.map((o) => [o.id, o.label]))}
                />
              </Field>
              {selectedOperator?.hint && <p className="text-[11px] text-gray-400 dark:text-gray-500 -mt-2.5 mb-3.5">{selectedOperator.hint}</p>}
              <Field label={needsTwoValues ? "Value range" : "Value"}>
                {selectedAttribute.type === "band" ? (
                  <div className="flex items-center gap-2">
                    <SelectField value={c.value || selectedAttribute.bands?.[0] || ""} onChange={(v) => onChange({ value: v })} options={selectedAttribute.bands || []} />
                    {needsTwoValues && (
                      <>
                        <span className="text-xs text-gray-400 shrink-0">to</span>
                        <SelectField value={c.value_to || selectedAttribute.bands?.[0] || ""} onChange={(v) => onChange({ value_to: v })} options={selectedAttribute.bands || []} />
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type={selectedAttribute.type === "number" ? "number" : "text"}
                      value={c.value || ""}
                      onChange={(e) => onChange({ value: e.target.value })}
                      min={selectedAttribute.range?.[0]}
                      max={selectedAttribute.range?.[1]}
                      className={inputCls}
                    />
                    {needsTwoValues && (
                      <>
                        <span className="text-xs text-gray-400 shrink-0">to</span>
                        <input
                          type={selectedAttribute.type === "number" ? "number" : "text"}
                          value={c.value_to || ""}
                          onChange={(e) => onChange({ value_to: e.target.value })}
                          min={selectedAttribute.range?.[0]}
                          max={selectedAttribute.range?.[1]}
                          className={inputCls}
                        />
                      </>
                    )}
                  </div>
                )}
              </Field>
            </>
          )}

          {selectedAttribute && c.operator && c.value !== undefined && c.value !== "" && (
            <AudiencePreviewPanel
              conditions={[{ attribute: c.attribute, operator: c.operator, value: needsTwoValues ? [c.value, c.value_to] : c.value }]}
            />
          )}

          <SectionLabel>Schedule</SectionLabel>
          <Field label="Runs">
            <SelectField value={selectedSchedule?.id || ""} onChange={setSchedule} options={data.schedules.map((s) => s.id)} labels={Object.fromEntries(data.schedules.map((s) => [s.id, s.label]))} />
          </Field>
          {selectedSchedule?.hint && <p className="text-[11px] text-gray-400 dark:text-gray-500 -mt-2.5 mb-3.5">{selectedSchedule.hint}</p>}
          {selectedSchedule?.fields.map((f) => (
            <Field key={f} label={f.replace(/_/g, " ")}>
              <input value={(c.schedule?.[f] ?? selectedSchedule.default[f]) ?? ""} onChange={(e) => setScheduleField(f, e.target.value)} className={inputCls} />
            </Field>
          ))}

          <SectionLabel>Re-entry</SectionLabel>
          <Field label="When a customer still matches next sweep">
            <SelectField value={selectedReEntry?.id || ""} onChange={setReEntry} options={data.re_entry.map((r) => r.id)} labels={Object.fromEntries(data.re_entry.map((r) => [r.id, r.label]))} />
          </Field>
          {selectedReEntry?.hint && <p className="text-[11px] text-gray-400 dark:text-gray-500 -mt-2.5 mb-3.5">{selectedReEntry.hint}</p>}
          {selectedReEntry?.fields?.map((f) => (
            <Field key={f} label={f.replace(/_/g, " ")}>
              <input value={(c.re_entry?.[f] ?? selectedReEntry.default?.[f]) ?? ""} onChange={(e) => setReEntryField(f, e.target.value)} className={inputCls} />
            </Field>
          ))}

          <Field label="Debounce (days, optional)">
            <input
              type="number"
              value={c.debounce_days ?? ""}
              onChange={(e) => onChange({ debounce_days: e.target.value ? Number(e.target.value) : undefined })}
              placeholder="e.g. 7 — ignore a re-cross within this many days"
              className={inputCls}
            />
          </Field>
        </>
      )}
    </div>
  );
};

const TriggerConfig: React.FC<{
  trigger: TriggerInstance;
  onChange: (patch: Record<string, any>) => void;
  journeyId?: string;
  savedJourneyId?: number;
  onPickTrigger?: (def: TriggerDef, category: JourneyCategory) => void;
  onTriggerDragStart?: (def: TriggerDef, category: JourneyCategory, x: number, y: number) => void;
  onTriggerDragMove?: (x: number, y: number) => void;
  onTriggerDragEnd?: (x: number, y: number) => void;
}> = ({ trigger, onChange, journeyId, savedJourneyId, onPickTrigger, onTriggerDragStart, onTriggerDragMove, onTriggerDragEnd }) => {
  const def = findTriggerDef(trigger.id);
  // Per direct ask: Workflow Settings (both Execution priority and
  // Execution timeout) only makes sense for a journey that's actually
  // time/priority-sensitive — kept just on Fraud / Anomaly Alert (journeyId
  // "fraud_anomaly_alert", confirmed real journey id) and removed from every
  // other trigger card.
  const showWorkflowSettings = journeyId === "fraud_anomaly_alert";

  // No trigger picked yet — a genuinely-from-scratch journey (see
  // builder-page.tsx's initialTrigger) — show the real catalog picker
  // instead of Workflow Settings, which has nothing to configure yet.
  if (trigger.id === "" && onPickTrigger && onTriggerDragStart && onTriggerDragMove && onTriggerDragEnd) {
    return (
      <div>
        <ConfigHeader icon={Zap} title="Trigger" subtitle="Trigger description" />
        <TriggerPicker onPick={onPickTrigger} onDragStart={onTriggerDragStart} onDragMove={onTriggerDragMove} onDragEnd={onTriggerDragEnd} />
      </div>
    );
  }

  return (
    <div>
      <ConfigHeader icon={Zap} title={trigger.label || "Trigger"} subtitle={def?.description || "Trigger description"} />

      <TriggerBehaviorConfig trigger={trigger} onChange={onChange} savedJourneyId={savedJourneyId} journeyId={journeyId} />

      {showWorkflowSettings && (
        <>
          <SectionLabel>Workflow Settings</SectionLabel>
          <Field label="Execution priority">
            <SelectField value={trigger.config?.priority || "Highest"} onChange={(v) => onChange({ priority: v })} options={["Highest", "High", "Medium", "Low"]} />
          </Field>
          <Field label="Execution timeout">
            <SelectField
              value={trigger.config?.timeout || "Execute immediately { < 1 second }"}
              onChange={(v) => onChange({ timeout: v })}
              options={["Execute immediately { < 1 second }", "After 1 minute", "After 5 minutes", "After 1 hour"]}
            />
          </Field>
        </>
      )}
    </div>
  );
};

// ─── Node config ────────────────────────────────────────────────────────────

// ── AI Classify / AI Decide (Figma, dormancy reactivation) ──────────────────
// "Classify"/"Decide" fill the tier/option cards from the AI instruction.
// There's no backend endpoint for this yet (GET /openapi.json has only
// /v1/bot/ai-generate, which writes message copy), so the suggestion is
// worked out here from the instruction's own wording — every card stays
// editable, and the engine gets whatever is left in them.

const AI_SUGGEST_DELAY_MS = 900;

const AiSuggestSkeleton: React.FC<{ count: number; or?: boolean }> = ({ count, or }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <React.Fragment key={i}>
        {or && i > 0 && <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 text-center my-2">OR</p>}
        <div className={`rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 p-4 animate-pulse ${or ? "" : "mb-3"}`}>
          <div className="h-4 w-28 rounded bg-gray-300/70 dark:bg-gray-600 mb-5" />
          <div className="flex gap-3">
            <div className="h-4 flex-1 rounded bg-gray-300/70 dark:bg-gray-600" />
            <div className="h-4 flex-1 rounded bg-gray-300/50 dark:bg-gray-600" />
            <div className="h-4 flex-1 rounded bg-gray-300/70 dark:bg-gray-600" />
          </div>
        </div>
      </React.Fragment>
    ))}
  </>
);

const AiSectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="flex items-center gap-2 text-[11px] font-semibold tracking-wide text-gray-400 dark:text-gray-500 mt-6 mb-3 uppercase">
    {children} <Sparkles size={14} className="text-indigo-500" />
  </p>
);

const aiButtonCls =
  "mt-1 px-3.5 py-2 rounded-md text-sm font-medium text-white bg-indigo-700 hover:bg-indigo-800 disabled:opacity-80 disabled:cursor-wait transition-colors";

type Tier = { id: string; label: string; operator: string; value: string; value2?: string };
type DecideOption = { id: string; label: string; description: string };

// Money-shaped instructions get the design's balance tiers; anything else
// gets a plain High/Medium/Low split to fill in.
function suggestTiers(instruction: string, prev: Tier[], attributeId = ""): Tier[] {
  const data = isDataAttribute(attributeId) || /data[\s_]balance|\bMB\b/i.test(instruction);
  const overdue = isOverdueAttribute(attributeId) || /overdue/i.test(instruction);
  const inactive = isInactivityAttribute(attributeId) || /inactiv/i.test(instruction);
  const money = /balance|amount|deposit|income|salary|spend/i.test(instruction);
  // Overdue / inactivity: the design's thresholds (3 / 10 / 20 days) as
  // ranges so a customer lands in exactly one tier.
  const base: Omit<Tier, "id">[] = inactive && !isOverdueAttribute(attributeId) && !isDataAttribute(attributeId)
    ? [
        { label: "recently inactive", operator: "between", value: "4", value2: "10" },
        { label: "inactive", operator: "between", value: "11", value2: "20" },
        { label: "severe inactive", operator: "above", value: "20" },
      ]
    : overdue && !isDataAttribute(attributeId)
    ? [
        { label: "recently overdue", operator: "between", value: "4", value2: "10" },
        { label: "overdue", operator: "between", value: "11", value2: "20" },
        { label: "severe overdue", operator: "above", value: "20" },
      ]
    : data
    ? [
        { label: "low", operator: "between", value: "101", value2: "500" },
        { label: "critical", operator: "between", value: "1", value2: "100" },
        { label: "exhausted", operator: "equal", value: "0" },
      ]
    : money
    ? [
        { label: "Tier 3 — Premium", operator: "above", value: "100000" },
        { label: "Tier 2 — Plus", operator: "between", value: "25000", value2: "100000" },
        { label: "Tier 1 — Standard", operator: "below", value: "25000" },
      ]
    : [
        { label: "High", operator: "above", value: "" },
        { label: "Medium", operator: "between", value: "", value2: "" },
        { label: "Low", operator: "below", value: "" },
      ];
  return base.map((t, i) => ({ id: prev[i]?.id || `tier_${i + 1}`, ...t }));
}

// "…receive a savings offer, a fixed-deposit offer, or no offer" →
// Savings, Fixed Deposit. Option ids are reused by position so a branching
// Decide keeps each path's steps.
// Data-balance / bundle decisions return the telco journey's fixed outputs.
const TELCO_DECIDE_OPTIONS: Omit<DecideOption, "id">[] = [
  { label: "recommend_bundle", description: "Recommend one eligible data bundle" },
  { label: "no_offer", description: "No suitable eligible bundle, so send nothing" },
  { label: "refresh_profile", description: "Balance is missing or stale, so refresh the profile first" },
  { label: "manual_review", description: "Inconsistent data, so log for manual review" },
];

// Bill-overdue decisions: remind only when the billing data is verified.
const BILL_OVERDUE_DECIDE_OPTIONS: Omit<DecideOption, "id">[] = [
  { label: "send_reminder", description: "Bill is verified overdue, so send a payment reminder" },
  { label: "no_message", description: "Bill is paid or payment status is uncertain, so send nothing" },
  { label: "refresh_profile", description: "Billing data is missing or stale, so refresh the profile first" },
  { label: "manual_review", description: "Inconsistent billing data, so log for manual review" },
];

function suggestOptions(instruction: string, prev: DecideOption[]): DecideOption[] {
  if (/overdue|bill|payment|reminder/i.test(instruction)) {
    return BILL_OVERDUE_DECIDE_OPTIONS.map((o, i) => ({ id: prev[i]?.id || `opt_${i + 1}`, ...o }));
  }
  if (/bundle|data[\s_]balance|\bdata\b/i.test(instruction)) {
    return TELCO_DECIDE_OPTIONS.map((o, i) => ({ id: prev[i]?.id || `opt_${i + 1}`, ...o }));
  }
  const found: string[] = [];
  const re = /\b(?:an?\s+)?([a-z][\w-]*(?:\s+[a-z][\w-]*)?)\s+offer\b/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(instruction))) {
    const phrase = m[1].replace(/^(?:(?:or|and|a|an|the|receive|recommend|get)\s+)+/i, "").trim();
    if (!phrase || /^(?:no|none|any|an?|the)$/i.test(phrase) || /\bno$/i.test(phrase)) continue;
    const label = phrase.replace(/-/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
    if (!found.includes(label)) found.push(label);
  }
  if (found.length === 0) return prev;
  return found.map((label, i) => ({
    id: prev[i]?.id || `opt_${i + 1}`,
    label,
    description: `Recommend a ${label.toLowerCase()}${/savings/i.test(label) ? " account" : ""} offer`,
  }));
}

const groupMoney = (digits: string) => (digits ? Number(digits).toLocaleString("en-IN") : "");

const MoneyInput: React.FC<{ value: string; onChange: (v: string) => void; symbol: string }> = ({ value, onChange, symbol }) => (
  <span className="flex items-center text-sm font-semibold text-gray-800 dark:text-gray-100">
    <span>{symbol}</span>
    <input
      value={groupMoney(value)}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
      inputMode="numeric"
      placeholder="0"
      size={Math.max(4, groupMoney(value).length)}
      className="bg-transparent outline-none min-w-0 placeholder-gray-400"
    />
  </span>
);

const AiClassifyPanel: React.FC<{
  header: React.ReactNode;
  config: Record<string, any>;
  onChange: (patch: Record<string, any>) => void;
  attributes: { id: string; label: string }[];
  operators: { id: string; label: string }[];
  attributesLoading: boolean;
}> = ({ header, config: c, onChange, attributes, operators, attributesLoading }) => {
  const [classifying, setClassifying] = React.useState(false);
  const symbol = useDefaultCountryCode("+234") === "+91" ? "₹" : "₦";
  const tiers: Tier[] = c.tiers || [];
  const setTiers = (next: Tier[]) => onChange({ tiers: next });
  const updateTier = (i: number, patch: Partial<Tier>) => setTiers(tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const operatorIds = operators.length ? operators.map((o) => o.id) : ["above", "below", "between", "equal"];
  const operatorLabels = operators.length ? Object.fromEntries(operators.map((o) => [o.id, o.label])) : {};
  const attributeId = c.classifyAttribute || attributes[0]?.id || "";
  const isData = isDataAttribute(attributeId);
  const isOverdue = isOverdueAttribute(attributeId) || isInactivityAttribute(attributeId);
  const valueSymbol = isData || isOverdue ? "" : symbol;
  // Keep a saved attribute (e.g. a template's current_data_balance_mb)
  // selectable even when condition-options doesn't list it.
  const attributeOptions =
    attributeId && !attributes.some((a) => a.id === attributeId)
      ? [{ id: attributeId, label: c.classifyAttributeLabel || attributeId }, ...attributes]
      : attributes;

  const classify = () => {
    setClassifying(true);
    setTimeout(() => {
      setTiers(suggestTiers(c.instruction || "", tiers, attributeId));
      setClassifying(false);
    }, AI_SUGGEST_DELAY_MS);
  };

  return (
    <div>
      {header}
      <SectionLabel>INPUT</SectionLabel>
      <Field label="What should AI classify?">
        {attributesLoading && attributeOptions.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5 py-2">
            <Loader2 size={12} className="animate-spin" /> Loading…
          </p>
        ) : (
          <SelectField
            value={attributeId}
            onChange={(v) => onChange({ classifyAttribute: v, classifyAttributeLabel: attributeOptions.find((a) => a.id === v)?.label || v })}
            options={attributeOptions.map((a) => a.id)}
            labels={Object.fromEntries(attributeOptions.map((a) => [a.id, a.label]))}
          />
        )}
      </Field>
      <Field label="AI instruction">
        <textarea
          value={c.instruction || ""}
          onChange={(e) => onChange({ instruction: e.target.value })}
          rows={6}
          placeholder="e.g. Classify the supplied balance into exactly one of the configured tiers."
          className={`${inputCls} resize-none leading-relaxed`}
        />
      </Field>
      <button type="button" onClick={classify} disabled={classifying || !(c.instruction || "").trim()} className={aiButtonCls}>
        {classifying ? "Classifying..." : "Classify"}
      </button>

      {(classifying || tiers.length > 0) && <AiSectionTitle>AI classify based on</AiSectionTitle>}
      {classifying ? (
        <AiSuggestSkeleton count={3} />
      ) : (
        tiers.map((tier, i) => (
          <div key={tier.id} className="group mb-3 rounded-xl bg-gray-100/80 dark:bg-gray-800/60 p-3.5">
            <div className="flex items-center justify-between gap-2 mb-3">
              <input
                value={tier.label}
                onChange={(e) => updateTier(i, { label: e.target.value })}
                placeholder="Tier name"
                size={Math.max(8, tier.label.length)}
                className="bg-white dark:bg-gray-900 rounded-md px-2 py-1 text-sm font-semibold text-gray-800 dark:text-gray-100 outline-none focus:ring-2 focus:ring-indigo-200 min-w-0"
              />
              {tiers.length > 1 && (
                <button type="button" onClick={() => setTiers(tiers.filter((_, idx) => idx !== i))} className="text-gray-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Remove tier">
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-1 text-sm font-semibold text-gray-700 dark:text-gray-200">
              <span>{attributeId || "value"}</span>
              <span className="relative flex items-center">
                <select
                  value={tier.operator || operatorIds[0]}
                  onChange={(e) => updateTier(i, { operator: e.target.value })}
                  className="appearance-none bg-transparent pr-5 font-semibold outline-none cursor-pointer"
                >
                  {operatorIds.map((o) => (
                    <option key={o} value={o}>
                      {operatorLabels[o] ?? o}
                    </option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-0 pointer-events-none" />
              </span>
              <MoneyInput value={tier.value} onChange={(v) => updateTier(i, { value: v })} symbol={valueSymbol} />
              {tier.operator === "between" && (
                <>
                  <span>-</span>
                  <MoneyInput value={tier.value2 || ""} onChange={(v) => updateTier(i, { value2: v })} symbol={valueSymbol} />
                </>
              )}
              {isData && <span className="text-xs font-medium text-gray-400 dark:text-gray-500">MB</span>}
              {isOverdue && <span className="text-xs font-medium text-gray-400 dark:text-gray-500">days</span>}
            </div>
          </div>
        ))
      )}
      {!classifying && tiers.length > 0 && (
        <button
          type="button"
          onClick={() => setTiers([...tiers, { id: `tier_${Date.now().toString(36)}`, label: "", operator: "above", value: "" }])}
          className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          + Add another tier
        </button>
      )}
    </div>
  );
};

// The design's "Choose the most appropriate" list. A saved value outside it
// is kept as its own option rather than lost.
const DECIDE_DIMENSIONS = ["Offer type", "Product", "Channel", "Next best action"];
// Backend `basis` ids shown by name (churn_risk_escalation decides on lifetime_value).
const DECIDE_DIMENSION_LABELS: Record<string, string> = { lifetime_value: "Customer value" };

const AiDecidePanel: React.FC<{
  header: React.ReactNode;
  config: Record<string, any>;
  onChange: (patch: Record<string, any>) => void;
}> = ({ header, config: c, onChange }) => {
  const [deciding, setDeciding] = React.useState(false);
  const options: DecideOption[] = c.options || [];
  const setOptions = (next: DecideOption[]) => onChange({ options: next });
  const updateOption = (i: number, patch: Partial<DecideOption>) => setOptions(options.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  const dimension = c.decideAttribute || DECIDE_DIMENSIONS[0];
  const dimensions = DECIDE_DIMENSIONS.includes(dimension) ? DECIDE_DIMENSIONS : [dimension, ...DECIDE_DIMENSIONS];
  const hasOptions = options.some((o) => o.label || o.description);

  const decide = () => {
    setDeciding(true);
    setTimeout(() => {
      setOptions(suggestOptions(c.instruction || "", options));
      setDeciding(false);
    }, AI_SUGGEST_DELAY_MS);
  };

  return (
    <div>
      {header}
      <SectionLabel>WHAT SHOULD AI DECIDE?</SectionLabel>
      <Field label="Choose the most appropriate">
        <SelectField value={dimension} onChange={(v) => onChange({ decideAttribute: v })} options={dimensions} labels={DECIDE_DIMENSION_LABELS} />
      </Field>
      <Field label="AI instruction">
        <textarea
          value={c.instruction || ""}
          onChange={(e) => onChange({ instruction: e.target.value })}
          rows={6}
          placeholder="e.g. Choose whether the customer should receive a savings offer, a fixed-deposit offer, or no offer."
          className={`${inputCls} resize-none leading-relaxed`}
        />
      </Field>
      <button type="button" onClick={decide} disabled={deciding || !(c.instruction || "").trim()} className={aiButtonCls}>
        {deciding ? "Deciding..." : "Decide"}
      </button>

      {(deciding || hasOptions) && <AiSectionTitle>AI generated options</AiSectionTitle>}
      {deciding ? (
        <AiSuggestSkeleton count={2} or />
      ) : (
        hasOptions &&
        options.map((opt, i) => (
          <React.Fragment key={opt.id}>
            {i > 0 && <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 text-center my-2">OR</p>}
            <div className="group rounded-xl bg-gray-100/80 dark:bg-gray-800/60 p-3.5">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-gray-400 dark:text-gray-500 w-6 shrink-0">#{i + 1}</span>
                <input
                  value={opt.label}
                  onChange={(e) => updateOption(i, { label: e.target.value })}
                  placeholder="Option name"
                  size={Math.max(8, opt.label.length)}
                  className="bg-white dark:bg-gray-900 rounded-md px-2 py-1 text-sm font-semibold text-gray-800 dark:text-gray-100 outline-none focus:ring-2 focus:ring-indigo-200 min-w-0"
                />
                {options.length > 1 && (
                  <button type="button" onClick={() => setOptions(options.filter((_, idx) => idx !== i))} className="ml-auto text-gray-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Remove option">
                    <X size={14} />
                  </button>
                )}
              </div>
              <input
                value={opt.description}
                onChange={(e) => updateOption(i, { description: e.target.value })}
                placeholder="Description"
                className="mt-2 ml-9 w-[calc(100%-2.25rem)] bg-transparent text-sm text-gray-700 dark:text-gray-200 outline-none placeholder-gray-400"
              />
            </div>
          </React.Fragment>
        ))
      )}
      {!deciding && hasOptions && (
        <button
          type="button"
          onClick={() => setOptions([...options, { id: `opt_${Date.now().toString(36)}`, label: "", description: "" }])}
          className="mt-3 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          + Add another option
        </button>
      )}
    </div>
  );
};

const NodeConfig: React.FC<{
  node: FlowNode;
  onChange: (patch: Record<string, any>) => void;
  onChangeType?: (newType: string) => void;
  triggerLabel: string;
  precedingType?: string;
  precedingNode?: FlowNode;
  journeyId?: string;
}> = ({ node, onChange, onChangeType, triggerLabel, precedingType, precedingNode, journeyId }) => {
  const def = findActionDef(node.type);
  const c = node.config;

  const header = <ConfigHeader icon={NODE_ICONS[node.type] || HelpCircle} title={node.label} subtitle={def?.description} />;

  const [emailModalOpen, setEmailModalOpen] = React.useState(false);

  // Real selectors — GET /templates/?channel=WhatsApp (core connect API) and
  // GET /v1/bot/sms-campaigns — fired whenever the matching Send node type is
  // selected, REGARDLESS of a preceding AI Generate step: WhatsApp can only
  // ever send a pre-approved template (Meta rejects free text outside the
  // 24h window), and SMS previews always go through a selected campaign —
  // AI Generate only fills the template's variables, it never replaces
  // picking one.
  // Also enabled for ai_generate/ai_moderate — their new "Fallback template"
  // dropdowns (Figma redesign, 20 Oct) reuse this same real template list
  // rather than a second fetch.
  // Also enabled for the generic "Send Message" card once its own Channel
  // selector (c.channel) picks the matching channel — GET /v1/bot/templates
  // ?channel= is still "unconfigured"/empty for whatsapp and sms live (and
  // 400s outright for rcs), so that card reuses these same real per-channel
  // sources the dedicated Send<Channel> cards already use, rather than a
  // second endpoint that currently can't serve two of the three channels it
  // offers.
  const { templates: whatsappTemplates, loading: templatesLoading } = useWhatsAppTemplates(
    node.type === "send_whatsapp" ||
      node.type === "ai_generate" ||
      node.type === "ai_moderate" ||
      (node.type === "send_message" && c.channel === "WhatsApp")
  );
  // GET /v1/bot/sms-templates — the template carries its own body/variables/
  // length/segments (used by both this dropdown and the phone preview below),
  // unlike /v1/bot/sms-campaigns (a tenant's send campaigns, a different
  // concept: how an SMS goes out, not what it says).
  const { data: smsTemplatesResult, loading: smsTemplatesLoading } = useSmsTemplates(
    node.type === "send_sms" || node.type === "ai_generate" || (node.type === "send_message" && c.channel === "SMS")
  );
  const smsTemplates = smsTemplatesResult?.templates || [];
  const { templates: emailTemplates, loading: emailTemplatesLoading } = useEmailTemplates(
    node.type === "send_email" || (node.type === "send_message" && c.channel === "Email")
  );
  // GET /v1/bot/push-templates — same template-carries-its-own-body pattern
  // as sms-templates, for the Send Push dropdown + notification preview.
  const { data: pushTemplatesResult, loading: pushTemplatesLoading } = usePushTemplates(node.type === "send_push");
  const pushTemplates = pushTemplatesResult?.templates || [];
  // GET /v1/bot/variables — cached at module level (journey-api.ts), so
  // calling it again here (ai_generate's "Required variables" checklist,
  // Figma redesign 20 Oct) is free rather than a second fetch.
  const { groups: variableGroups } = useVariables();

  // Real GET /v1/bot/profile-attributes?journey= — the Fetch Profile card's
  // grouped checkboxes, scoped to whichever journey this bot was started
  // from. Seeds the node's `fetch` (checked attribute ids) and `fetchLabels`
  // (id -> label, so flow-canvas's summary line doesn't need its own fetch)
  // once, the first time this loads for a node that hasn't been customized.
  const { data: profileAttrs, loading: profileAttrsLoading } = useProfileAttributes(node.type === "fetch_profile", journeyId);
  // A telco Fetch Profile starts on Subscriber ID instead of the generic
  // Customer ID default — once, and never over a key the user picked.
  const telco = isTelcoContext(journeyId, triggerLabel);
  React.useEffect(() => {
    if (node.type === "fetch_profile" && telco && !c.lookupKeyChosen && (!c.findCustomerBy || c.findCustomerBy === "Customer ID")) {
      onChange({ findCustomerBy: "Subscriber ID" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id, telco]);
  React.useEffect(() => {
    if (!profileAttrs || node.type !== "fetch_profile") return;
    const labels: Record<string, string> = {};
    const unavailableIds = new Set<string>();
    profileAttrs.groups.forEach((g) =>
      g.attributes.forEach((a) => {
        labels[a.id] = a.label;
        if (a.available === false) unavailableIds.add(a.id);
      })
    );
    const patch: Record<string, any> = {};
    // `defaults` can include an attribute that's `available: false` (confirmed
    // live, e.g. lifetime_value_tier) — pre-checking it here would silently
    // seed a fetch condition that never matches, exactly what the disabled
    // checkbox above exists to prevent for a manual tick.
    if (!c.fetch) patch.fetch = profileAttrs.defaults.filter((id) => !unavailableIds.has(id));
    if (!c.fetchLabels) patch.fetchLabels = labels;
    if (Object.keys(patch).length) onChange(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileAttrs]);

  // "See More" per-group collapse (Figma redesign, 20 Oct) — pure UI, no
  // backend dependency; collapses a group past this many attributes.
  const [expandedProfileGroups, setExpandedProfileGroups] = React.useState<Set<string>>(new Set());
  const PROFILE_GROUP_COLLAPSE_AT = 6;

  // Real GET /v1/bot/assigners — the Assign People card's assignee dropdown
  // and its pre-filled follow-up message. Also backs Create CRM Task's queue
  // dropdown (its `kind: "queue"` entries, e.g. "rm_call_queue"/"RM call
  // queue") — one real list for "who/where this account gets handed to",
  // same shape either way. Seeds defaults once, the first time this loads
  // for a node that hasn't been customized.
  const { data: assigners, loading: assignersLoading } = useAssigners(node.type === "assign_people" || node.type === "create_crm_task");
  React.useEffect(() => {
    if (!assigners) return;
    if (node.type === "create_crm_task") {
      const patch: Record<string, any> = {};
      if (!c.queueLabel) {
        // A preset's own queue (e.g. churn's "rm_retention") is kept even
        // when it isn't in the placeholder assigner list.
        const match = c.queue ? assigners.assigners.find((a) => a.id === c.queue) : assigners.assigners.find((a) => a.kind === "queue");
        patch.queue = c.queue || match?.id;
        patch.queueLabel = match?.label || humanizeId(c.queue || "");
      }
      if (!c.message) patch.message = assigners.default_message;
      if (!c.delivery) patch.delivery = "immediately";
      if (Object.keys(patch).length) onChange(patch);
      return;
    }
    if (node.type !== "assign_people") return;
    const patch: Record<string, any> = {};
    if (!c.assignerId && assigners.assigners[0]) {
      patch.assignerId = assigners.assigners[0].id;
      patch.assignerLabel = assigners.assigners[0].label;
    }
    if (!c.message) patch.message = assigners.default_message;
    if (Object.keys(patch).length) onChange(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigners]);

  // Real GET /v1/bot/condition-options — the Condition card's "CHECK If"
  // attribute dropdown, each attribute's own allowed values, and the
  // TRUE/FALSE operator list. Seeds `booleanCheckLabel` (the human label for
  // whatever `booleanCheck` id is already set, e.g. from a loaded journey
  // template) once, so flow-canvas's summary line can show "Loan application
  // status" instead of the raw "loan_application_abandoned_status" id.
  // Also used by AI Classify's "What should AI classify?" + per-tier operator
  // pickers — same real attribute/operator vocabulary, reused rather than
  // inventing a second fixed list.
  const { data: conditionOptions, loading: conditionOptionsLoading } = useConditionOptions(
    node.type === "condition" || node.type === "ai_classify"
  );
  React.useEffect(() => {
    if (!conditionOptions || node.type !== "condition") return;
    const match = conditionOptions.attributes.find((a) => a.id === c.booleanCheck);
    if (match && c.booleanCheckLabel !== match.label) onChange({ booleanCheckLabel: match.label });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditionOptions, c.booleanCheck]);

  // Auto-migrate an existing (old-shape) condition node to the new named-group
  // UI the moment it's opened — this is what makes an already-published
  // journey's condition nodes (e.g. Salary-Day Loan's "consent_status"/
  // "loan_eligibility" checks, imported from the real backend with a flat
  // true/false shape) show the same redesigned panel a brand-new condition
  // node gets, instead of only brand-new nodes ever seeing it. Lossless: the
  // reconciliation in updateNodeConfig (flow-model.ts) carries the node's
  // real true/false subtrees over onto the migrated group / on-failure
  // branch, so nothing downstream gets dropped. Keyed on node.id so it runs
  // once per node, not on every keystroke while editing it.
  React.useEffect(() => {
    if (node.type !== "condition" || isGroupedCondition(c)) return;
    onChange({ _migrateCondition: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id, node.type]);

  // Real POST /v1/bot/ai-generate, called directly from this panel (not just
  // as part of a full Test Run) — fetches a profile first (no phone/unique_id
  // available here outside a Test Run, so this gets whatever the backend's
  // own default/demo profile resolves to), then generates real content and
  // writes it straight into this node's own preview fields. {{customer.*}}
  // tokens come back exactly as the API returns them — never substituted here.
  // No real customer phone/unique_id exists in this static builder view (that
  // only shows up inside an actual Test Run), and fetch-profile genuinely
  // requires one of those — so this skips it and generates from an empty
  // profile rather than hitting a guaranteed 400.
  const { generate } = useAiGenerate();

  // Shared by the AI Generate node's own "Re-generate" and a connected Send
  // node's "Regenerate Again" below — same real call, just sourced from
  // whichever node actually owns the prompt/tone/length/channels, and written
  // into whichever node's own content fields should receive it.
  const runGenerate = async (promptConfig: Record<string, any>, applyTo: (patch: Record<string, any>) => void) => {
    if (!promptConfig.prompt?.trim()) {
      toast.error("Add what you want to generate first on the AI Generate step.");
      return false;
    }
    const channels = ((promptConfig.channels?.length ? promptConfig.channels : [promptConfig.channel || "WhatsApp"]) as string[])
      .map((ch) => CHANNEL_TO_API[ch])
      .filter(Boolean) as JourneyChannel[];
    if (channels.length === 0) {
      toast.error("Pick at least one channel first on the AI Generate step.");
      return false;
    }
    const { result, unavailable } = await generate({
      instruction: promptConfig.prompt,
      profile: {},
      channels,
      tone: promptConfig.tone,
      length: promptConfig.length,
    });
    if (result) {
      const { whatsapp, sms, email } = result.channels;
      const patch: Record<string, any> = {};
      if (whatsapp) {
        patch.description = whatsapp.body;
        if (whatsapp.cta_text) patch.ctaText1 = whatsapp.cta_text;
      }
      if (sms) patch.messageBody = sms.body;
      if (email) {
        patch.title = email.subject;
        if (patch.description === undefined) patch.description = email.html_body;
      }
      applyTo(patch);
      toast.success(`Generated real content via ${result.provider}.`);
    } else if (unavailable) {
      toast.error("The AI provider isn't configured on the backend yet.");
    } else {
      toast.error("Couldn't generate content — try again.");
    }
    return true;
  };

  const [regenerating, setRegenerating] = React.useState(false);
  const handleRegenerate = async () => {
    setRegenerating(true);
    await runGenerate(c, onChange);
    setRegenerating(false);
  };

  // A Send node fed by a preceding AI Generate step has no prompt/tone/length
  // of its own — those live on that AI Generate node, so this reads from
  // `precedingNode.config` instead of `c`, but still writes the regenerated
  // content into *this* Send node's own fields (same as the real Test Run
  // pipeline's applyGenerated in builder-page.tsx).
  const [regeneratingSend, setRegeneratingSend] = React.useState(false);
  const handleRegenerateFromPreceding = async () => {
    if (!precedingNode) return;
    setRegeneratingSend(true);
    await runGenerate(precedingNode.config || {}, onChange);
    setRegeneratingSend(false);
  };

  const fetchChecklist = (
    <>
      <SectionLabel>Fetch</SectionLabel>
      <Checkbox checked={!!c.fetchName} onChange={(v) => onChange({ fetchName: v })} label="Name" />
      <Checkbox checked={!!c.fetchBusinessInfo} onChange={(v) => onChange({ fetchBusinessInfo: v })} label="Business Info" />
      <Checkbox checked={!!c.fetchReadyToOnboard} onChange={(v) => onChange({ fetchReadyToOnboard: v })} label="Ready to Onboard?" />
    </>
  );

  // Grouped checklist for the Fetch Profile action, rendered entirely from
  // GET /v1/bot/profile-attributes (see the hook above) — group names and
  // attributes both come from the response, nothing here is hardcoded, so a
  // journey with different groups (e.g. "Loan information" instead of "KYC
  // status") just renders as-is. Distinct from the flat `fetchChecklist`
  // above, which Assign Agent's "Fetch" action still uses.
  const fetchIds: string[] = c.fetch || profileAttrs?.defaults || [];
  const fetchProfileChecklist = profileAttrsLoading && !profileAttrs ? (
    <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5">
      <Loader2 size={12} className="animate-spin" /> Loading fields…
    </p>
  ) : (
    profileAttrs?.groups.map((group) => {
      const expanded = expandedProfileGroups.has(group.group);
      const visibleAttrs = expanded ? group.attributes : group.attributes.slice(0, PROFILE_GROUP_COLLAPSE_AT);
      return (
        <React.Fragment key={group.group}>
          <SectionLabel>{group.group}</SectionLabel>
          {visibleAttrs.map((attr) => {
            const unavailable = attr.available === false;
            return (
              <div key={attr.id} className="flex items-center gap-2">
                <Checkbox
                  checked={!unavailable && fetchIds.includes(attr.id)}
                  onChange={(v) => onChange({ fetch: v ? [...fetchIds, attr.id] : fetchIds.filter((id) => id !== attr.id) })}
                  label={attr.label}
                  disabled={unavailable}
                />
                {/* Real `recommended` field (confirmed live via
                    ?design=abbas) — NOT every `default: true` attribute is
                    recommended (e.g. last_name/phone/email are defaults but
                    not flagged recommended), so this reads the attribute's
                    own flag rather than deriving it from the defaults list. */}
                {attr.recommended && (
                  <span className="text-[10px] font-semibold text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-500/10 px-1.5 py-0.5 rounded">Recommend</span>
                )}
                {/* `available: false` means this isn't an actual warehouse
                    column yet — ticking it would build a fetch condition
                    that silently never matches, so it's disabled outright. */}
                {unavailable && <span className="text-[10px] text-gray-400 dark:text-gray-500">Not available yet</span>}
              </div>
            );
          })}
          {group.attributes.length > PROFILE_GROUP_COLLAPSE_AT && (
            <button
              type="button"
              onClick={() =>
                setExpandedProfileGroups((prev) => {
                  const next = new Set(prev);
                  if (expanded) next.delete(group.group);
                  else next.add(group.group);
                  return next;
                })
              }
              className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline mb-2"
            >
              {expanded ? "See less" : "See More"}
            </button>
          )}
        </React.Fragment>
      );
    })
  );

  switch (node.type) {
    case "fetch_profile":
    case "fetch_order":
    case "fetch_ticket":
      return (
        <div>
          {header}
          {node.type === "fetch_profile" ? (
            <>
              {/* Figma (fraud alert): one Lookup key instead of source / find
                  by / value. UI-only like those were — the engine looks the
                  contact up from the run itself. Same `findCustomerBy` key,
                  so saved bots keep their choice. */}
              <Field label="Lookup key">
                <SelectField
                  value={c.findCustomerBy || (telco ? "Subscriber ID" : "Customer ID")}
                  onChange={(v) => onChange({ findCustomerBy: v, lookupKeyChosen: true })}
                  options={Object.keys(LOOKUP_KEYS)}
                  labels={Object.fromEntries(Object.entries(LOOKUP_KEYS).map(([label, key]) => [label, `${label}   “${key}”`]))}
                />
              </Field>
              <div className="mt-4 rounded-lg border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50/40 dark:bg-indigo-500/5 px-3 py-3">{fetchProfileChecklist}</div>
              <div className="mt-5">
                <SectionLabel>ON FAILURE</SectionLabel>
                {/* UI-only, no confirmed live failure-policy field yet (catalog.ts
                    defaults onFailure: "retry_then_stop") — a single fixed option
                    rather than a real menu, same "UI-only pending backend"
                    precedent as ai_moderate's checks below. */}
                <Radio checked={c.onFailure === "retry_then_stop"} onChange={() => onChange({ onFailure: "retry_then_stop" })} label="Retry, then stop and log failure" />
              </div>
            </>
          ) : (
          <>
          <SectionLabel>Generate For</SectionLabel>
          <Field label="Select profile source">
            <SelectField value={c.profileSource || triggerLabel} onChange={(v) => onChange({ profileSource: v })} options={[triggerLabel, "Manual Entry"]} />
          </Field>
          <Field label="Find customer by">
            <SelectField
              value={c.findCustomerBy}
              onChange={(v) => onChange({ findCustomerBy: v })}
              options={["Customer ID", "Lead ID", "Phone", "Email"]}
            />
          </Field>
          <Field label="Value">
            <input
              value={c.value || `{{${triggerLabel}.customer_id}}`}
              onChange={(e) => onChange({ value: e.target.value })}
              className={inputCls}
            />
          </Field>
          </>
          )}
        </div>
      );

    case "segment":
      return (
        <div>
          {header}
          <SectionLabel>Conditions</SectionLabel>
          <Field label="Segment Name">
            <input value={c.segmentName} onChange={(e) => onChange({ segmentName: e.target.value })} placeholder="e.g. High Value Customer" className={inputCls} />
          </Field>
          {(c.conditions || []).map((cond: any, i: number) => (
            <React.Fragment key={i}>
              {i > 0 && <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 my-2">and</p>}
              <Field label="Value">
                <div className="space-y-2">
                  <input
                    value={cond.attribute}
                    onChange={(e) => {
                      const next = [...c.conditions];
                      next[i] = { ...next[i], attribute: e.target.value };
                      onChange({ conditions: next });
                    }}
                    placeholder="e.g. Order value"
                    className={inputCls}
                  />
                  <input
                    value={cond.comparator}
                    onChange={(e) => {
                      const next = [...c.conditions];
                      next[i] = { ...next[i], comparator: e.target.value };
                      onChange({ conditions: next });
                    }}
                    placeholder="e.g. > 3"
                    className={inputCls}
                  />
                </div>
              </Field>
            </React.Fragment>
          ))}
          <button
            type="button"
            onClick={() => onChange({ conditions: [...(c.conditions || []), { attribute: "", comparator: "" }] })}
            className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            + Add another source
          </button>
        </div>
      );

    case "update_profile":
      return (
        <div>
          {header}
          <Field label="Attribute">
            <input value={c.attribute} onChange={(e) => onChange({ attribute: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Value">
            <input value={c.value} onChange={(e) => onChange({ value: e.target.value })} className={inputCls} />
          </Field>
        </div>
      );

    case "ai_generate": {
      // A journey imported from GET /v1/bot/journeys/{id} stores its channel
      // lowercase ("whatsapp" — confirmed live), not this picker's own
      // capitalized vocabulary ("WhatsApp"). Normalizing here means the
      // checkbox below shows the right one already checked, and toggling it
      // off actually removes it instead of silently re-adding a second,
      // differently-cased copy.
      const normalizeChannel = (ch: string) => CHANNEL_OPTIONS_ALL.find((opt) => opt.toLowerCase() === ch.toLowerCase()) || ch;
      const channels: string[] = (c.channels?.length ? c.channels : [c.channel || "WhatsApp"]).map(normalizeChannel);
      const toggleChannel = (ch: string) => {
        const next = channels.includes(ch) ? channels.filter((x: string) => x !== ch) : [...channels, ch];
        const kept = next.length ? next : [ch];
        onChange({ channels: kept, channel: kept[0] });
      };
      const previewChannel = channels[0] || "WhatsApp";
      const fallbackTemplateOptions = previewChannel === "SMS" ? smsTemplates.map((t) => t.id) : whatsappTemplates.map((t) => t.template_name);
      const fallbackTemplateLabels =
        previewChannel === "SMS"
          ? Object.fromEntries(smsTemplates.map((t) => [t.id, t.name]))
          : Object.fromEntries(whatsappTemplates.map((t) => [t.template_name, t.template_name]));
      const allVariables = variableGroups.flatMap((g) => g.items);
      const requiredVariables: string[] = c.requiredVariables || [];
      const constraints: string[] = c.constraints || [];
      const CONSTRAINT_OPTIONS = [
        { id: "approved_claims_only", label: "Use approved claims and terms only" },
        { id: "preserve_disclosures", label: "Preserve required disclosures" },
        { id: "no_invented_terms", label: "Do not invent rates, limits or benefits" },
        { id: "fallback_for_missing", label: "Use fallback copy for missing optional data" },
      ];

      return (
        <div>
          {header}
          <SectionLabel>Content</SectionLabel>
          {/* "Content mode" (Figma redesign, 20 Oct) — UI-only, no confirmed
              backend field yet distinguishing these two paths. */}
          <Radio
            checked={(c.contentMode || "fill_approved_template") === "fill_approved_template"}
            onChange={() => onChange({ contentMode: "fill_approved_template" })}
            label="Fill only approved template variables"
          />
          <Radio checked={c.contentMode === "generate_new"} onChange={() => onChange({ contentMode: "generate_new" })} label="Generate a new" />
          {(c.contentMode || "fill_approved_template") === "fill_approved_template" && (
            <Field label="Fallback template">
              <SelectField
                value={c.fallbackTemplateId || ""}
                onChange={(v) => onChange({ fallbackTemplateId: v })}
                options={fallbackTemplateOptions}
                labels={fallbackTemplateLabels}
              />
            </Field>
          )}
          <Field label="What exactly do you want to personalised?">
            <textarea value={c.prompt || ""} onChange={(e) => onChange({ prompt: e.target.value })} rows={3} className={`${inputCls} resize-none`} />
          </Field>
          {/* Required for this card to publish — confirmed live on every
              shipped journey's own AI Generate node (e.g. salary_day_loan's
              "banking_offer_personalise_v1"). A fresh card is seeded with a
              placeholder default (catalog.ts) so it isn't blank; shown here
              so the operator can point it at the real id for this journey
              instead of publishing with the placeholder. */}
          <Field label="Prompt template ID">
            <input
              value={c.prompt_template_id || ""}
              onChange={(e) => onChange({ prompt_template_id: e.target.value })}
              placeholder="e.g. banking_offer_personalise_v1"
              className={inputCls}
            />
          </Field>
          <div className="grid grid-cols-2 gap-2.5">
            <Field label="Tone">
              <SelectField value={c.tone} onChange={(v) => onChange({ tone: v })} options={["Friendly", "Formal", "Casual", "Urgent"]} />
            </Field>
            <Field label="Length">
              <SelectField value={c.length} onChange={(v) => onChange({ length: v })} options={["Short (< 200 chars)", "Medium (200-500 chars)", "Long (500+ chars)"]} />
            </Field>
          </div>
          <button
            type="button"
            disabled={regenerating}
            onClick={handleRegenerate}
            className="flex items-center gap-1.5 mb-4 px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 rounded-lg"
          >
            {regenerating && <Loader2 size={14} className="animate-spin" />} Re-generate
          </button>

          <SectionLabel>Generate For</SectionLabel>
          <Field label="Select Channels">
            <ChannelMultiSelect selected={channels} onToggle={toggleChannel} />
          </Field>

          <Checkbox checked={!!c.keepWithinLimits} onChange={(v) => onChange({ keepWithinLimits: v })} label={`Keep within ${previewChannel} limits`} />
          <Checkbox checked={!!c.includeFirstName} onChange={(v) => onChange({ includeFirstName: v })} label="Include customer's first name" />
          <Checkbox checked={!!c.includeCta} onChange={(v) => onChange({ includeCta: v })} label="Include CTA" />

          {allVariables.length > 0 && (
            <>
              <SectionLabel>Required variables</SectionLabel>
              {allVariables.map((v) => (
                <Checkbox
                  key={v.id}
                  checked={requiredVariables.includes(v.id)}
                  onChange={(checked) => onChange({ requiredVariables: checked ? [...requiredVariables, v.id] : requiredVariables.filter((id) => id !== v.id) })}
                  label={v.label}
                />
              ))}
            </>
          )}

          {/* "Constraints" (Figma redesign, 20 Oct) — UI-only, no confirmed
              backend schema for this list yet. */}
          <SectionLabel>Constraints</SectionLabel>
          {CONSTRAINT_OPTIONS.map((opt) => (
            <Checkbox
              key={opt.id}
              checked={constraints.includes(opt.id)}
              onChange={(checked) => onChange({ constraints: checked ? [...constraints, opt.id] : constraints.filter((id) => id !== opt.id) })}
              label={opt.label}
            />
          ))}

          <SectionLabel>Delivery</SectionLabel>
          <Radio checked={c.delivery === "immediately"} onChange={() => onChange({ delivery: "immediately" })} label="Send immediately" />
          <Radio checked={c.delivery === "after"} onChange={() => onChange({ delivery: "after" })} label="Send After" />
          {c.delivery === "after" && (
            <div className="flex items-center gap-2 mt-2 ml-6">
              <input type="number" min={1} value={c.sendAfterValue} onChange={(e) => onChange({ sendAfterValue: Number(e.target.value) })} className={`${inputCls} w-20`} />
              <SelectField value={c.sendAfterUnit} onChange={(v) => onChange({ sendAfterUnit: v })} options={["min", "hrs", "days"]} />
            </div>
          )}

          <MessagePreviewEditor channel={previewChannel} config={c} onChange={onChange} onRegenerate={handleRegenerate} regenerating={regenerating} />
        </div>
      );
    }

    case "ai_moderate": {
      // Real GET /v1/bot/journeys/dormancy_reactivation shows this node with
      // an empty config live — no backend checks schema confirmed yet, so
      // these 5 labels are a fixed UI list (matches the reference design),
      // stored as an id array like Fetch Profile's own checklist.
      const MODERATE_CHECKS = [
        { id: "regulatory_claims", label: "Regulatory claims" },
        { id: "financial_promises", label: "Financial promises" },
        { id: "unsupported_claims", label: "Unsupported claims" },
        { id: "restricted_language", label: "Restricted language" },
        { id: "required_disclosures", label: "Required disclosures" },
      ];
      const checks: string[] = c.checks || [];
      const moderateFallbackOptions = whatsappTemplates.map((t) => t.template_name);
      const moderateFallbackLabels = Object.fromEntries(whatsappTemplates.map((t) => [t.template_name, t.template_name]));
      return (
        <div>
          {header}
          <SectionLabel>Checks</SectionLabel>
          {MODERATE_CHECKS.map((item) => (
            <Checkbox
              key={item.id}
              checked={checks.includes(item.id)}
              onChange={(v) => onChange({ checks: v ? [...checks, item.id] : checks.filter((id) => id !== item.id) })}
              label={item.label}
            />
          ))}

          {/* "On Rejection" (Figma redesign, 20 Oct) — additive, UI-only (same
              empty-config precedent noted above for Checks). */}
          <SectionLabel>On Rejection</SectionLabel>
          <Radio
            checked={(c.onRejection || "fallback_template") === "fallback_template"}
            onChange={() => onChange({ onRejection: "fallback_template" })}
            label="Use pre-approved static fallback template"
          />
          {(c.onRejection || "fallback_template") === "fallback_template" && (
            <Field label="Select pre-approved template">
              <SelectField
                value={c.fallbackTemplateId || ""}
                onChange={(v) => onChange({ fallbackTemplateId: v })}
                options={moderateFallbackOptions}
                labels={moderateFallbackLabels}
              />
            </Field>
          )}
          <Radio checked={c.onRejection === "stop_and_log"} onChange={() => onChange({ onRejection: "stop_and_log" })} label="Stop and log for review" />
        </div>
      );
    }

    case "ai_classify":
      return (
        <AiClassifyPanel
          header={header}
          config={c}
          onChange={onChange}
          attributes={conditionOptions?.attributes || []}
          operators={conditionOptions?.operators || []}
          attributesLoading={conditionOptionsLoading}
        />
      );

    case "ai_decide":
      return <AiDecidePanel header={header} config={c} onChange={onChange} />;

    case "ai_format":
    case "ai_summarize":
      return (
        <div>
          {header}
          <Field label="Instruction">
            <textarea value={c.instruction} onChange={(e) => onChange({ instruction: e.target.value })} rows={4} className={`${inputCls} resize-none`} />
          </Field>
        </div>
      );

    case "send_message":
    case "send_email":
    case "send_whatsapp":
    case "send_rcs":
    case "send_sms":
    case "send_push": {
      const aiConnected = precedingType === "ai_generate";
      const CHANNEL_BY_TYPE: Record<string, string> = { send_email: "Email", send_whatsapp: "WhatsApp", send_rcs: "RCS", send_sms: "SMS", send_push: "Push" };
      const channel = CHANNEL_BY_TYPE[node.type] || c.channel || "WhatsApp";
      const selectedWaTemplate =
        node.type === "send_whatsapp" || (node.type === "send_message" && c.channel === "WhatsApp")
          ? whatsappTemplates.find((t) => t.template_name === c.template)
          : undefined;
      const selectedEmailTemplate =
        node.type === "send_email" || (node.type === "send_message" && c.channel === "Email")
          ? emailTemplates.find((t) => t.id === c.template)
          : undefined;
      // Shared by the inline dropdown and the Edit Email modal's Templates
      // gallery — same backfill either way (and clears the Email Editor's own
      // custom design, since picking a catalog template supersedes it).
      const handleEmailTemplateSelect = (templateId: string) => {
        const found = emailTemplates.find((t) => t.id === templateId);
        onChange({
          template: templateId,
          templateLabel: found?.template_name,
          title: found?.subject || c.title,
          description: found?.plain_text || c.description,
          // Kept alongside `description` (plain text) so the real send step
          // (builder-page.tsx) can use the template's own designed HTML as
          // content.email.html_body instead of re-wrapping its plain text in
          // the default design — a catalog template already has a design.
          templateHtml: found?.html || "",
          useCustomDesign: false,
        });
      };

      // Per direct ask: a dedicated Send<Channel> card (e.g. Send SMS) was
      // otherwise stuck on whichever channel it started as — the only way to
      // change it was deleting the node and adding a fresh one. This lets the
      // operator switch it in place; changeSendNodeType (flow-model.ts)
      // resets the template (channel-specific, so an SMS template id means
      // nothing on an Email node) but carries over any manually-typed body
      // text into the new channel's own body field.
      const CHANNEL_TO_TYPE: Record<string, string> = { WhatsApp: "send_whatsapp", Email: "send_email", SMS: "send_sms", RCS: "send_rcs", Push: "send_push" };

      return (
        <div>
          {header}
          {node.type === "send_message" ? (
            <>
              <SectionLabel>Generate For</SectionLabel>
              <Field label="Select Channel">
                <SelectField value={c.channel} onChange={(v) => onChange({ channel: v })} options={["WhatsApp", "Email", "SMS", "RCS"]} />
              </Field>
            </>
          ) : (
            onChangeType && (
              <Field label="Channel">
                <SelectField
                  value={channel}
                  onChange={(v) => {
                    const newType = CHANNEL_TO_TYPE[v];
                    if (newType && newType !== node.type) onChangeType(newType);
                  }}
                  options={["WhatsApp", "Email", "SMS", "RCS", "Push"]}
                />
              </Field>
            )
          )}
          <SectionLabel>Content</SectionLabel>
          {node.type === "send_whatsapp" || (node.type === "send_message" && c.channel === "WhatsApp") ? (
            <Field label="Select message template">
              {templatesLoading ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5 py-1">
                  <Loader2 size={12} className="animate-spin" /> Loading approved templates…
                </p>
              ) : (
                <SelectField
                  value={c.template || ""}
                  onChange={(v) => onChange({ template: v, templateId: whatsappTemplates.find((t) => t.template_name === v)?.id })}
                  options={["", ...whatsappTemplates.map((t) => t.template_name)]}
                />
              )}
              {!templatesLoading && whatsappTemplates.length === 0 && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1.5">No approved WhatsApp templates found for this tenant yet.</p>
              )}
              {aiConnected && (
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">
                  • Since AI-Generated node is connected, its content will fill this template's variables — WhatsApp still requires an approved template either way.
                </p>
              )}
            </Field>
          ) : node.type === "send_sms" || (node.type === "send_message" && c.channel === "SMS") ? (
            <Field label="Select message template">
              {smsTemplatesLoading ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5 py-1">
                  <Loader2 size={12} className="animate-spin" /> Loading templates…
                </p>
              ) : (
                <SelectField
                  value={c.template || ""}
                  onChange={(v) => {
                    const found = smsTemplates.find((t) => t.id === v);
                    onChange({ template: v, templateLabel: found?.name, messageBody: found?.body ?? c.messageBody });
                  }}
                  options={["", ...smsTemplates.map((t) => t.id)]}
                  labels={Object.fromEntries(smsTemplates.map((t) => [t.id, t.name]))}
                />
              )}
              {!smsTemplatesLoading && smsTemplates.length === 0 && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1.5">No SMS templates found yet.</p>
              )}
              {aiConnected && (
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">• Since AI-Generated node is connected, its content will fill this template's message body.</p>
              )}
            </Field>
          ) : node.type === "send_email" || (node.type === "send_message" && c.channel === "Email") ? (
            <Field label="Select message template">
              {emailTemplatesLoading ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5 py-1">
                  <Loader2 size={12} className="animate-spin" /> Loading templates…
                </p>
              ) : (
                <SelectField
                  value={c.template || ""}
                  onChange={handleEmailTemplateSelect}
                  options={["", ...emailTemplates.map((t) => t.id)]}
                  labels={Object.fromEntries(emailTemplates.map((t) => [t.id, t.template_name]))}
                />
              )}
              {!emailTemplatesLoading && emailTemplates.length === 0 && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1.5">No email templates found for this tenant yet.</p>
              )}
              {aiConnected && (
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">• Since AI-Generated node is connected, its content will fill this template's variables.</p>
              )}
            </Field>
          ) : node.type === "send_push" ? (
            <Field label="Select message template">
              {pushTemplatesLoading ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5 py-1">
                  <Loader2 size={12} className="animate-spin" /> Loading templates…
                </p>
              ) : (
                <SelectField
                  value={c.template || ""}
                  onChange={(v) => {
                    const found = pushTemplates.find((t) => t.id === v);
                    onChange({ template: v, templateLabel: found?.name, title: found?.title ?? c.title, messageBody: found?.body ?? c.messageBody });
                  }}
                  options={["", ...pushTemplates.map((t) => t.id)]}
                  labels={Object.fromEntries(pushTemplates.map((t) => [t.id, t.name]))}
                />
              )}
              {!pushTemplatesLoading && pushTemplates.length === 0 && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1.5">No push templates found yet.</p>
              )}
              {aiConnected && (
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">• Since AI-Generated node is connected, its content will fill this template's message body.</p>
              )}
            </Field>
          ) : (
            // Reached only by RCS (the dedicated Send RCS card, or the
            // generic Send Message card with RCS picked) or Send Message
            // before any channel is picked — there's no real RCS template
            // source anywhere yet (GET /v1/bot/templates?channel=rcs 400s
            // live: "unknown channel"), so this shows that honestly instead
            // of the made-up "Welcome Message/Order Update/Follow Up" list
            // that used to render here regardless of channel.
            <Field label="Select message template">
              {node.type === "send_message" && !c.channel ? (
                <p className="text-[11px] text-gray-400 dark:text-gray-500 py-1">Pick a channel above to see its templates.</p>
              ) : (
                <p className="text-[11px] text-amber-600 dark:text-amber-400 py-1">No RCS templates available yet.</p>
              )}
            </Field>
          )}

          <SectionLabel>Delivery</SectionLabel>
          <Radio checked={c.delivery === "immediately"} onChange={() => onChange({ delivery: "immediately" })} label="Send immediately" />
          <Radio checked={c.delivery === "after"} onChange={() => onChange({ delivery: "after" })} label="Send After" />
          {c.delivery === "after" && (
            <div className="flex items-center gap-2 mt-2 ml-6">
              <input
                type="number"
                min={1}
                value={c.sendAfterValue}
                onChange={(e) => onChange({ sendAfterValue: Number(e.target.value) })}
                className={`${inputCls} w-20`}
              />
              <SelectField value={c.sendAfterUnit} onChange={(v) => onChange({ sendAfterUnit: v })} options={["min", "hrs", "days"]} />
            </div>
          )}

          <MessagePreviewEditor
            channel={channel}
            config={c}
            onChange={onChange}
            waTemplate={selectedWaTemplate}
            emailTemplate={selectedEmailTemplate}
            onRegenerate={aiConnected && precedingNode ? handleRegenerateFromPreceding : undefined}
            regenerating={regeneratingSend}
            onOpenEmailModal={node.type === "send_email" ? () => setEmailModalOpen(true) : undefined}
            templateName={
              node.type === "send_whatsapp" || (node.type === "send_message" && c.channel === "WhatsApp")
                ? c.template || undefined
                : node.type === "send_sms" ||
                    node.type === "send_email" ||
                    node.type === "send_push" ||
                    (node.type === "send_message" && (c.channel === "SMS" || c.channel === "Email"))
                  ? c.templateLabel || undefined
                  : aiConnected
                    ? c.contentName || "generated_content"
                    : c.template || undefined
            }
          />

          {node.type === "send_email" && (
            <EmailActionModal
              isOpen={emailModalOpen}
              onClose={() => setEmailModalOpen(false)}
              config={c}
              onChange={onChange}
              emailTemplates={emailTemplates}
              emailTemplatesLoading={emailTemplatesLoading}
              onSelectTemplate={handleEmailTemplateSelect}
            />
          )}
        </div>
      );
    }

    case "make_voice_call":
      return (
        <div>
          {header}
          <Field label="Script">
            <textarea value={c.script} onChange={(e) => onChange({ script: e.target.value })} rows={4} className={`${inputCls} resize-none`} />
          </Field>
        </div>
      );

    case "assign_agent":
      return (
        <div>
          {header}
          <SectionLabel>Assign To</SectionLabel>
          <Field label="Select agent">
            <SelectField value={c.agent} onChange={(v) => onChange({ agent: v })} options={["Sales agent", "Support agent", "Onboarding agent"]} />
          </Field>
          <SectionLabel>Agent Action</SectionLabel>
          <SelectField value={c.agentAction} onChange={(v) => onChange({ agentAction: v })} options={["Fetch", "Update"]} />
          {c.agentAction === "Fetch" && <div className="mt-4">{fetchChecklist}</div>}
        </div>
      );

    case "assign_people": {
      // Real GET /v1/bot/assigners (see the hook above) — who the case can go
      // to, plus the follow-up message/variables the design pre-fills. No more
      // Sales/Support/Amanda/David placeholders.
      const assignerOptions = assigners?.assigners || [];
      const insertVar = assigners?.variables?.[0] || "customer.name";
      return (
        <div>
          {header}
          <SectionLabel>Content</SectionLabel>
          <Field label="Select Assigner">
            {assignersLoading && !assigners ? (
              <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5 py-2">
                <Loader2 size={12} className="animate-spin" /> Loading assigners…
              </p>
            ) : (
              <SelectField
                value={c.assignerId || assignerOptions[0]?.id || ""}
                onChange={(v) => onChange({ assignerId: v, assignerLabel: assignerOptions.find((a) => a.id === v)?.label || v })}
                options={assignerOptions.map((a) => a.id)}
                labels={Object.fromEntries(assignerOptions.map((a) => [a.id, a.label]))}
              />
            )}
          </Field>
          <Field label="Message for Assigner">
            <textarea value={c.message || ""} onChange={(e) => onChange({ message: e.target.value })} rows={6} className={`${inputCls} resize-none`} />
          </Field>
          <div className="flex items-center justify-between -mt-2.5 mb-3.5">
            <span className="text-[10px] text-gray-400 dark:text-gray-500">{(c.message || "").length} Characters</span>
            <button
              type="button"
              onClick={() => onChange({ message: `${c.message || ""}{{${insertVar}}}` })}
              className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              Add variable
            </button>
          </div>
          <SectionLabel>Delivery</SectionLabel>
          <Radio checked={c.delivery === "immediately"} onChange={() => onChange({ delivery: "immediately" })} label="Send immediately" />
          <Radio checked={c.delivery === "after"} onChange={() => onChange({ delivery: "after" })} label="Send After" />
          {/* Was missing here even though catalog.ts already defaults
              sendAfterValue/sendAfterUnit — every other Send panel renders
              this same block when "Send After" is picked (e.g. line ~1683). */}
          {c.delivery === "after" && (
            <div className="flex items-center gap-2 mt-2 ml-6">
              <input type="number" min={1} value={c.sendAfterValue} onChange={(e) => onChange({ sendAfterValue: Number(e.target.value) })} className={`${inputCls} w-20`} />
              <SelectField value={c.sendAfterUnit} onChange={(v) => onChange({ sendAfterUnit: v })} options={["min", "hrs", "days"]} />
            </div>
          )}
        </div>
      );
    }

    case "create_crm_task": {
      // Shown as the design's Assign People card. The engine step is still
      // create_crm_task ({queue, priority} — GET /v1/bot/actions); whoever is
      // picked goes out as its `queue`. Every GET /v1/bot/assigners entry is
      // offered (teams and queues), plus the step's own queue if it isn't one.
      const all = assigners?.assigners || [];
      const options = c.queue && !all.some((a) => a.id === c.queue) ? [{ id: c.queue, label: c.queueLabel || humanizeId(c.queue) }, ...all] : all;
      const insertVar = assigners?.variables?.[0] || "customer.name";
      return (
        <div>
          {header}
          <SectionLabel>Content</SectionLabel>
          <Field label="Select Assigner">
            {assignersLoading && !assigners ? (
              <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5 py-2">
                <Loader2 size={12} className="animate-spin" /> Loading assigners…
              </p>
            ) : (
              <SelectField
                value={c.queue || options[0]?.id || ""}
                onChange={(v) => onChange({ queue: v, queueLabel: options.find((q) => q.id === v)?.label || v })}
                options={options.map((q) => q.id)}
                labels={Object.fromEntries(options.map((q) => [q.id, q.label]))}
              />
            )}
          </Field>
          <Field label="Message for Assigner">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 focus-within:ring-2 focus-within:ring-indigo-200 dark:focus-within:ring-indigo-500/20">
              <textarea
                value={c.message || ""}
                onChange={(e) => onChange({ message: e.target.value })}
                rows={8}
                className="w-full px-3 pt-2.5 text-sm bg-transparent text-gray-900 dark:text-gray-100 outline-none resize-none leading-relaxed"
              />
              <div className="flex items-center justify-between px-3 pb-2.5">
                <span className="text-[11px] text-gray-400 dark:text-gray-500">{(c.message || "").length} Characters</span>
                <button
                  type="button"
                  onClick={() => onChange({ message: `${c.message || ""}{{${insertVar}}}` })}
                  className="px-2.5 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Add variable
                </button>
              </div>
            </div>
          </Field>
          <SectionLabel>Delivery</SectionLabel>
          <Radio checked={(c.delivery || "immediately") === "immediately"} onChange={() => onChange({ delivery: "immediately" })} label="Send immediately" />
          <Radio checked={c.delivery === "after"} onChange={() => onChange({ delivery: "after" })} label="Send After" />
          {c.delivery === "after" && (
            <div className="flex items-center gap-2 mt-2 ml-6">
              <input type="number" min={1} value={c.sendAfterValue ?? 1} onChange={(e) => onChange({ sendAfterValue: Number(e.target.value) })} className={`${inputCls} w-20`} />
              <SelectField value={c.sendAfterUnit || "min"} onChange={(v) => onChange({ sendAfterUnit: v })} options={["min", "hrs", "days"]} />
            </div>
          )}
        </div>
      );
    }

    case "create_support_ticket":
      return (
        <div>
          {header}
          <SectionLabel>Priority</SectionLabel>
          <Radio checked={c.priority === "high"} onChange={() => onChange({ priority: "high" })} label="High" />
          <Radio checked={c.priority === "medium"} onChange={() => onChange({ priority: "medium" })} label="Medium" />
          <Radio checked={c.priority === "low"} onChange={() => onChange({ priority: "low" })} label="Low" />
          <Field label="Notes">
            <textarea value={c.notes} onChange={(e) => onChange({ notes: e.target.value })} rows={3} className={`${inputCls} resize-none mt-3`} />
          </Field>
        </div>
      );

    case "create_lead":
      return (
        <div>
          {header}
          <Field label="Source">
            <SelectField value={c.source} onChange={(v) => onChange({ source: v })} options={["Website Form", "Referral", "Ad Campaign", "Manual"]} />
          </Field>
        </div>
      );

    case "escalate":
      return (
        <div>
          {header}
          <Field label="Reason">
            <textarea value={c.reason} onChange={(e) => onChange({ reason: e.target.value })} rows={3} className={`${inputCls} resize-none`} />
          </Field>
        </div>
      );

    case "fallback":
      return (
        <div>
          {header}
          <Field label="Message">
            <textarea value={c.message} onChange={(e) => onChange({ message: e.target.value })} rows={3} className={`${inputCls} resize-none`} />
          </Field>
        </div>
      );

    case "end_workflow":
      return (
        <div>
          {header}
          <p className="text-xs text-gray-400 dark:text-gray-500">This step has no further configuration — the journey stops here.</p>
        </div>
      );

    // Both share the same True/False canvas paths (BRANCHING_TYPES in
    // catalog.ts), so their config panels are laid out on top of the same
    // trueBehavior/falseBehavior toggles — see setBranchBehavior in
    // flow-model.ts, which builder-page.tsx routes those two keys through.
    case "condition": {
      // New Figma named-group shape (20 Oct) — a freshly-created condition
      // node starts here (catalog.ts); an old flat rule/boolean node migrates
      // into this shape the first time it's edited through this UI (never on
      // load, so an untouched old journey is unaffected). Each group maps
      // 1:1 to one of this node's real canvas branches (optionBranches,
      // reusing the same mechanism AI Decide already uses) — see
      // flow-canvas.tsx/flow-model.ts.
      if (isGroupedCondition(c)) {
        const groups: ConditionGroup[] = c.conditionGroups;
        const endWorkflowOnFailure = !!c.endWorkflowOnFailure;
        const liveAttrs = conditionOptions?.attributes || [];
        const liveOps = conditionOptions?.operators || [];
        const endWorkflowAllowed = conditionOptions?.end_workflow_allowed !== false;

        const updateGroups = (next: ConditionGroup[]) => onChange({ conditionGroups: next });
        const updateGroup = (gi: number, patch: Partial<ConditionGroup>) => updateGroups(groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)));
        const updateRow = (gi: number, ri: number, patch: Partial<ConditionRow>) =>
          updateGroup(gi, { rows: groups[gi].rows.map((r, i) => (i === ri ? { ...r, ...patch } : r)) });
        const addRow = (gi: number) => {
          const firstAttr = liveAttrs[0];
          updateGroup(gi, { rows: [...groups[gi].rows, { id: `row_${Date.now().toString(36)}`, attribute: firstAttr?.id || "", operator: liveOps[0]?.id || "", value: "" }] });
        };
        const removeRow = (gi: number, ri: number) => updateGroup(gi, { rows: groups[gi].rows.filter((_, i) => i !== ri) });
        const addGroup = () => {
          const firstAttr = liveAttrs[0];
          updateGroups([
            ...groups,
            {
              id: `group_${Date.now().toString(36)}`,
              name: `Condition ${groups.length + 1}`,
              matchLogic: "all",
              rows: [{ id: "row_0", attribute: firstAttr?.id || "", operator: liveOps[0]?.id || "", value: "" }],
            },
          ]);
        };
        const removeGroup = (gi: number) => updateGroups(groups.filter((_, i) => i !== gi));

        return (
          <div>
            {header}
            {conditionOptionsLoading && !conditionOptions ? (
              <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5 py-2">
                <Loader2 size={12} className="animate-spin" /> Loading…
              </p>
            ) : (
              <>
                {groups.map((group, gi) => {
                  const valueOptionsFor = (attrId: string) => liveAttrs.find((a) => a.id === attrId)?.values;
                  const attrFor = (attrId: string) => liveAttrs.find((a) => a.id === attrId);
                  return (
                    <React.Fragment key={group.id}>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">Group {gi + 1}</p>
                      <div className="rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 px-3.5 py-3.5 mb-3">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-1.5 min-w-0 flex-1">
                          <input
                            value={group.name}
                            onChange={(e) => updateGroup(gi, { name: e.target.value })}
                            className="text-xs font-bold uppercase tracking-wide text-gray-700 dark:text-gray-200 bg-transparent border-b border-transparent hover:border-gray-300 dark:hover:border-gray-600 focus:border-indigo-400 focus:outline-none min-w-0 flex-1"
                          />
                          <Pencil size={11} className="text-gray-400 shrink-0" />
                        </div>
                        <div className="flex items-center gap-0.5 shrink-0 bg-gray-100 dark:bg-gray-900 rounded-md p-0.5">
                          <button
                            type="button"
                            onClick={() => updateGroup(gi, { matchLogic: "all" })}
                            className={`text-[10px] font-bold px-2 py-1 rounded ${group.matchLogic === "all" ? "bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm" : "text-gray-500 dark:text-gray-400"}`}
                          >
                            ALL
                          </button>
                          <button
                            type="button"
                            onClick={() => updateGroup(gi, { matchLogic: "any" })}
                            className={`text-[10px] font-bold px-2 py-1 rounded ${group.matchLogic === "any" ? "bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm" : "text-gray-500 dark:text-gray-400"}`}
                          >
                            ANY
                          </button>
                        </div>
                      </div>

                      {group.rows.map((row, ri) => {
                        const attr = attrFor(row.attribute || liveAttrs[0]?.id || "");
                        // A number compared with <, ≤, >, ≥ needs a number. The
                        // catalog's `values` for a balance are band words (low /
                        // critical / exhausted) — comparing 150 MB ≤ "critical"
                        // is false every time, so the branch never fires. Take a
                        // threshold instead; the band word is the group's name.
                        const numericCompare = isNumericAttribute(attr) && isComparisonOperator(row.operator || liveOps[0]?.id || "");
                        const valueOptions = numericCompare ? undefined : valueOptionsFor(row.attribute);
                        const notANumber = numericCompare && row.value !== "" && row.value != null && !Number.isFinite(Number(row.value));
                        return (
                          <div key={row.id}>
                            {ri > 0 && (
                              <p className="text-[10px] font-bold text-center text-gray-400 dark:text-gray-500 my-1.5">{group.matchLogic === "all" ? "AND" : "OR"}</p>
                            )}
                            <div className="rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-3.5 py-3 mb-1.5">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <label className="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 mb-1">CHECK IF</label>
                                  <SelectField
                                    value={row.attribute || liveAttrs[0]?.id || ""}
                                    onChange={(v) => {
                                      const next = attrFor(v);
                                      const threshold = next?.default_threshold?.amount;
                                      updateRow(gi, ri, { attribute: v, operator: liveOps[0]?.id || "", value: isNumericAttribute(next) && threshold != null ? String(threshold) : "" });
                                    }}
                                    options={liveAttrs.map((a) => a.id)}
                                    labels={Object.fromEntries(liveAttrs.map((a) => [a.id, a.label]))}
                                  />
                                </div>
                                {group.rows.length > 1 && (
                                  <button type="button" onClick={() => removeRow(gi, ri)} className="text-gray-300 hover:text-rose-500 shrink-0 mt-4">
                                    <X size={14} />
                                  </button>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <div className="flex-1">
                                  <label className="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 mb-1">OPERATOR</label>
                                  <SelectField
                                    value={row.operator || liveOps[0]?.id || ""}
                                    onChange={(v) => updateRow(gi, ri, { operator: v })}
                                    options={liveOps.map((o) => o.id)}
                                    labels={Object.fromEntries(liveOps.map((o) => [o.id, o.label]))}
                                  />
                                </div>
                                <div className="flex-1">
                                  <label className="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 mb-1">VALUE</label>
                                  {valueOptions ? (
                                    <SelectField value={row.value || ""} onChange={(v) => updateRow(gi, ri, { value: v })} options={valueOptions} />
                                  ) : numericCompare ? (
                                    <input
                                      type="number"
                                      inputMode="decimal"
                                      value={row.value || ""}
                                      placeholder={attr?.default_threshold ? String(attr.default_threshold.amount) : "e.g. 200"}
                                      onChange={(e) => updateRow(gi, ri, { value: e.target.value })}
                                      className={`${inputCls} ${notANumber ? "border-rose-400 dark:border-rose-500" : ""}`}
                                    />
                                  ) : (
                                    <input value={row.value || ""} onChange={(e) => updateRow(gi, ri, { value: e.target.value })} className={inputCls} />
                                  )}
                                </div>
                              </div>
                              {notANumber ? (
                                <p className="text-[11px] text-rose-600 dark:text-rose-400 mt-1.5">
                                  "{row.value}" isn't a number, so this check is false every time. Enter the threshold
                                  {attr?.default_threshold ? ` (e.g. ${attr.default_threshold.amount}${attr.default_threshold.unit ? ` ${attr.default_threshold.unit}` : ""})` : ""} and use the group name for the label.
                                </p>
                              ) : numericCompare && attr?.values?.length ? (
                                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">
                                  Enter the threshold here; name the group ({attr.values.join(" / ")}) for the branch label.
                                </p>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                      <button type="button" onClick={() => addRow(gi)} className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline mt-1.5">
                        + Add another source
                      </button>
                      {groups.length > 1 && (
                        <button type="button" onClick={() => removeGroup(gi)} className="text-xs font-semibold text-gray-400 hover:text-rose-500 hover:underline mt-1.5 ml-3">
                          Remove group
                        </button>
                      )}
                      </div>
                    </React.Fragment>
                  );
                })}
                <button
                  type="button"
                  onClick={addGroup}
                  className="w-full text-sm font-bold text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-xl py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Add another condition group
                </button>

                <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800">
                  <SectionLabel>On Failure</SectionLabel>
                  <Checkbox
                    checked={endWorkflowOnFailure}
                    onChange={(v) => onChange({ endWorkflowOnFailure: v })}
                    label="End Workflow"
                    disabled={!endWorkflowAllowed}
                  />
                  {!endWorkflowAllowed && (
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 -mt-1">Ending the workflow here isn't available for this condition type.</p>
                  )}
                </div>
              </>
            )}
          </div>
        );
      }

      const mode = c.mode || "rule";
      const conditions: { attribute: string; operator: string; value: string }[] = c.conditions || [
        { attribute: "Previous Purchase Count", operator: "Equal", value: "0" },
      ];
      const setConditions = (next: typeof conditions) => onChange({ conditions: next });
      const attributeOptions = ["Previous Purchase Count", "Customer segment", "Order value", "Response received"];
      const operatorOptions = ["Equal", "Not equal", "Greater than", "Less than"];

      // Real GET /v1/bot/condition-options (see the hook above) — backs the
      // "boolean" mode's CHECK If / TRUE / FALSE panel below.
      const liveAttributes = conditionOptions?.attributes || [];
      const liveOperators = conditionOptions?.operators || [];
      const valueOptions = liveAttributes.find((a) => a.id === c.booleanCheck)?.values;

      return (
        <div>
          {header}
          <SectionLabel>Logic Type</SectionLabel>
          <Radio checked={mode === "rule"} onChange={() => onChange({ mode: "rule" })} label="If / Else { AND / OR }" />
          <Radio checked={mode === "boolean"} onChange={() => onChange({ mode: "boolean" })} label="This or That [ True or False ]" />

          {mode === "rule" ? (
            <>
              {conditions.map((cond, i) => (
                <div key={i} className="mt-4">
                  <p className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 mb-1.5">If</p>
                  <Field label="When?">
                    <SelectField
                      value={cond.attribute}
                      onChange={(v) => setConditions(conditions.map((x, idx) => (idx === i ? { ...x, attribute: v } : x)))}
                      options={attributeOptions}
                    />
                  </Field>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1">
                      <label className="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 mb-1">Operator</label>
                      <SelectField
                        value={cond.operator}
                        onChange={(v) => setConditions(conditions.map((x, idx) => (idx === i ? { ...x, operator: v } : x)))}
                        options={operatorOptions}
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 mb-1">Value</label>
                      <input
                        value={cond.value}
                        onChange={(e) => setConditions(conditions.map((x, idx) => (idx === i ? { ...x, value: e.target.value } : x)))}
                        className={inputCls}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <ToggleRadio checked={c.trueBehavior === "end"} onChange={(v) => onChange({ trueBehavior: v ? "end" : "continue" })} label="End workflow" />
                    {conditions.length > 1 && (
                      <button type="button" onClick={() => setConditions(conditions.filter((_, idx) => idx !== i))} className="text-[11px] text-gray-400 hover:text-rose-500">
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setConditions([...conditions, { attribute: attributeOptions[0], operator: "Equal", value: "0" }])}
                className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline mt-2"
              >
                + Add another condition
              </button>

              <div className="mt-5">
                <p className="text-[11px] font-bold text-rose-500 mb-1.5">Else</p>
                <Field label="When?">
                  <SelectField value={c.elseWhen || ""} onChange={(v) => onChange({ elseWhen: v })} options={["", ...attributeOptions]} />
                </Field>
                <ToggleRadio checked={c.falseBehavior === "end"} onChange={(v) => onChange({ falseBehavior: v ? "end" : "continue" })} label="End workflow" />
              </div>

              <SectionLabel>Match Logic</SectionLabel>
              <Radio checked={(c.matchLogic || "all") === "all"} onChange={() => onChange({ matchLogic: "all" })} label="ALL conditions must match" />
              <Radio checked={c.matchLogic === "any"} onChange={() => onChange({ matchLogic: "any" })} label="ANY condition can match" />
            </>
          ) : (
            <>
              {/* Real GET /v1/bot/condition-options (see the hook above) —
                  "CHECK If" and the TRUE/FALSE operator+value pairs all come
                  from here now; the fixed attributeOptions/operatorOptions
                  list above is only the rule-mode fallback. */}
              <SectionLabel>Check If</SectionLabel>
              {conditionOptionsLoading && !conditionOptions ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5 py-2">
                  <Loader2 size={12} className="animate-spin" /> Loading…
                </p>
              ) : (
                <SelectField
                  value={c.booleanCheck || liveAttributes[0]?.id || ""}
                  onChange={(v) => onChange({ booleanCheck: v, booleanCheckLabel: liveAttributes.find((a) => a.id === v)?.label || v })}
                  options={liveAttributes.map((a) => a.id)}
                  labels={Object.fromEntries(liveAttributes.map((a) => [a.id, a.label]))}
                />
              )}

              <SectionLabel>True</SectionLabel>
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1">
                  <label className="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 mb-1">Operator</label>
                  <SelectField
                    value={c.booleanTrueOperator || liveOperators[0]?.id || ""}
                    onChange={(v) => onChange({ booleanTrueOperator: v })}
                    options={liveOperators.map((o) => o.id)}
                    labels={Object.fromEntries(liveOperators.map((o) => [o.id, o.label]))}
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 mb-1">Value</label>
                  {valueOptions ? (
                    <SelectField value={c.booleanTrueValue || ""} onChange={(v) => onChange({ booleanTrueValue: v })} options={valueOptions} />
                  ) : (
                    <input value={c.booleanTrueValue || ""} onChange={(e) => onChange({ booleanTrueValue: e.target.value })} className={inputCls} />
                  )}
                </div>
              </div>
              <ToggleRadio checked={c.trueBehavior === "end"} onChange={(v) => onChange({ trueBehavior: v ? "end" : "continue" })} label="End workflow" />

              <SectionLabel>False</SectionLabel>
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1">
                  <label className="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 mb-1">Operator</label>
                  <SelectField
                    value={c.booleanFalseOperator || liveOperators[0]?.id || ""}
                    onChange={(v) => onChange({ booleanFalseOperator: v })}
                    options={liveOperators.map((o) => o.id)}
                    labels={Object.fromEntries(liveOperators.map((o) => [o.id, o.label]))}
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-[10px] font-semibold text-gray-400 dark:text-gray-500 mb-1">Value</label>
                  {valueOptions ? (
                    <SelectField value={c.booleanFalseValue || ""} onChange={(v) => onChange({ booleanFalseValue: v })} options={valueOptions} />
                  ) : (
                    <input value={c.booleanFalseValue || ""} onChange={(e) => onChange({ booleanFalseValue: e.target.value })} className={inputCls} />
                  )}
                </div>
              </div>
              <ToggleRadio checked={c.falseBehavior === "end"} onChange={(v) => onChange({ falseBehavior: v ? "end" : "continue" })} label="End workflow" />
            </>
          )}
        </div>
      );
    }

    case "branch":
      // Real shape, confirmed against GET /v1/bot/journeys/fraud_anomaly_alert
      // — a parallel fan-out to real child nodes (e.g. Send SMS + Send Push,
      // both firing), not a per-branch condition. Matches the reference
      // design exactly: just the Branch Type radios, plus an End-workflow
      // toggle per path (added per direct user ask — a Branch node had no
      // way to deliberately end a path, unlike Condition/Wait, which meant
      // an intentionally-terminal path looked identical to an unwired one
      // and tripped the backend's "branch has no outgoing paths" test-run
      // blocker). Shares the same trueBehavior/falseBehavior keys and
      // setBranchBehavior plumbing as Condition — see builder-page.tsx.
      return (
        <div>
          {header}
          <SectionLabel>Branch Type</SectionLabel>
          <Radio checked={c.mode !== "parallel"} onChange={() => onChange({ mode: "run_parallel" })} label="Run Parallel" />
          <Radio checked={c.mode === "parallel"} onChange={() => onChange({ mode: "parallel" })} label="All paths execute simultaneously" />

          <SectionLabel>Path 1</SectionLabel>
          <ToggleRadio checked={c.trueBehavior === "end"} onChange={(v) => onChange({ trueBehavior: v ? "end" : "continue" })} label="End workflow" />

          <SectionLabel>Path 2</SectionLabel>
          <ToggleRadio checked={c.falseBehavior === "end"} onChange={(v) => onChange({ falseBehavior: v ? "end" : "continue" })} label="End workflow" />
        </div>
      );

    case "wait":
    case "wait_until_event":
      return (
        <div>
          {header}
          <SectionLabel>Wait For</SectionLabel>
          <div className="flex items-center gap-2">
            <input type="number" min={1} value={c.value} onChange={(e) => onChange({ value: Number(e.target.value) })} className={`${inputCls} w-20`} />
            <SelectField value={c.unit} onChange={(v) => onChange({ unit: v })} options={["min", "hrs", "days"]} />
          </div>
          <SectionLabel>Then</SectionLabel>
          <Radio checked={c.thenBehavior === "continue"} onChange={() => onChange({ thenBehavior: "continue" })} label="Continue workflow" />
          <Radio checked={c.thenBehavior === "end"} onChange={() => onChange({ thenBehavior: "end" })} label="END" />
        </div>
      );

    default:
      return (
        <div>
          {header}
          <p className="text-xs text-gray-400 dark:text-gray-500">No additional configuration for this step yet.</p>
        </div>
      );
  }
};

const ConfigPanel: React.FC<{
  trigger: TriggerInstance | null;
  selectedNode: FlowNode | null;
  awaitingInsertion: boolean;
  precedingType?: string;
  precedingNode?: FlowNode;
  journeyId?: string;
  savedJourneyId?: number;
  onChangeTrigger: (patch: Record<string, any>) => void;
  onChangeNode: (patch: Record<string, any>) => void;
  onChangeNodeType?: (newType: string) => void;
  onPickTrigger?: (def: TriggerDef, category: JourneyCategory) => void;
  onTriggerDragStart?: (def: TriggerDef, category: JourneyCategory, x: number, y: number) => void;
  onTriggerDragMove?: (x: number, y: number) => void;
  onTriggerDragEnd?: (x: number, y: number) => void;
}> = ({
  trigger,
  selectedNode,
  awaitingInsertion,
  precedingType,
  precedingNode,
  journeyId,
  savedJourneyId,
  onChangeTrigger,
  onChangeNode,
  onChangeNodeType,
  onPickTrigger,
  onTriggerDragStart,
  onTriggerDragMove,
  onTriggerDragEnd,
}) => {
  const [open, setOpen] = React.useState(true);

  return (
    <div className="w-[320px] shrink-0 bg-white dark:bg-gray-900 border-l border-gray-100 dark:border-gray-800 overflow-y-auto">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex items-center justify-between w-full px-5 py-4 text-sm font-semibold text-gray-400 dark:text-gray-500"
      >
        Actions
        <ChevronDown size={15} className={open ? "" : "-rotate-90"} />
      </button>

      {open && (
        <div className="px-5 pb-8">
          {awaitingInsertion ? (
            <div className="flex flex-col items-center text-center gap-2 py-10 text-gray-400 dark:text-gray-500">
              <HelpCircle size={22} />
              <p className="text-xs">Pick a node type from the left panel to add it here.</p>
            </div>
          ) : selectedNode ? (
            <NodeConfig
              node={selectedNode}
              onChange={onChangeNode}
              onChangeType={onChangeNodeType}
              triggerLabel={trigger?.label || "Trigger"}
              precedingType={precedingType}
              precedingNode={precedingNode}
              journeyId={journeyId}
            />
          ) : trigger ? (
            <TriggerConfig
              trigger={trigger}
              onChange={onChangeTrigger}
              journeyId={journeyId}
              savedJourneyId={savedJourneyId}
              onPickTrigger={onPickTrigger}
              onTriggerDragStart={onTriggerDragStart}
              onTriggerDragMove={onTriggerDragMove}
              onTriggerDragEnd={onTriggerDragEnd}
            />
          ) : (
            <p className="text-xs text-gray-400 dark:text-gray-500">Select a trigger to get started.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ConfigPanel;
